/**
 *  ÁNYK-SZABÁLYMOTOR – a NAV ÁNYK-sablon (.enyk) tartalmi szabályainak értelmezője.
 *
 *  Források: a sablon a backend/nav-enyk.js kimenete (szabályok AST-ként), a szerkezet a
 *  backend/nav-nyomtatvany-modell.js XSD-modellje. A két forrás mezőazonosítója azonos
 *  (XSD: Field_<fid>, ENYK: fid). Függőség nélküli: az Angular-alkalmazásban és Node-ban
 *  (esbuild-csomagként) is fut.
 *
 *  A FÜGGVÉNYEK JELENTÉSE: a NAV Általános Nyomtatványkitöltő (abevjava.jar, 2026-08-os kiadás)
 *  számolómotorja szerint (hu.piller.enykp.alogic.calculator: ABEVFeaturedBaseFunctions,
 *  FunctionBodies, NumericOperations, Calculator). Az ÁNYK függvényleírása nem publikus; a
 *  viselkedést a program kódjából olvastuk ki, az együttműködéshez szükséges mértékben. Ahol az
 *  ÁNYK a felületre hagyatkozik (lenyíló listák, feltételes értékadás), ott a szabály üzenetét
 *  igazzá tevő olvasatot választottuk – ezeket a függvénynél megjegyzés jelöli („ÉRTELMEZÉS”).
 *
 *  AZ ADAT: { Form_2608A: [ { <kod>: érték, Chain_X: [ {…} ], 'Chain_X#fej': {…}, '#aux': {…} } ] }
 *    – az ENYK-mezők egy része nincs az XSD-ben (lapjelzők, darabszámok, köztes értékek, kivonatolt
 *      fejlécek): ezek a példány (statikus lap) vagy a dinamikus lap láncsorának '#aux' objektumában
 *      élnek (a számított értékek ide íródnak; az XML-be nem kerülnek);
 *    – ENYK dinamikus lap (XXXX-es fid-ek) = az XSD külső lánca: a k-adik lappéldány a lánc
 *      k·R … k·R+R−1 sora (R = a lapon lévő sorok száma, pl. 08A-01-03: 3, 08M-06: 4, egyébként 1);
 *    – soros ENYK-mezők (0FXXXXD0521AA): az XSD-ben egyetlen mező (0FXXXXD0520AA, sor „520-522”)
 *      a belső láncban – az 521. sor a belső lánc 1. sora;
 *    – a dinamikus lap külső láncának fejléce ('Chain_X#fej') és a láncon kívüli mezője csak az első
 *      lappéldányhoz tartozik (az XSD-ben egy fejléc van);
 *    – az ENYK dátuma ééééhhnn szám, a logikai mező "true"/"X" – az adatban 'ÉÉÉÉ-HH-NN' és true.
 *
 *  A SABLONBÓL VÁRT MEZŐK (nav-enyk.js): metak[fid].alapertek (META defaultvalue – pl. a 2608M „egy”
 *  mező 1, erre épül az M-lapok számlálása), metak[fid].calcZe (a calc_ze attribútum léte: a 0 is
 *  „kitöltött”; ha hiányzik, igaznak vesszük), metak[fid].kerekites (round), szabaly.celKotes === false
 *  (target_bind="no"). Ugyanahhoz a fid-hez több <FIELDCALC> blokk tartozhat – a szabályaikat össze kell
 *  fűzni (a 2608-ban 44 blokk / 132 szabály, köztük a főlap adószám-ellenőrzései).
 */

/* ============================== NYILVÁNOS TÍPUSOK ============================== */

export interface AnykHiba {
  form: string; peldany: number; ut: string; mezo: string | null; lap: string | null;
  uzenet: string; szint: 'error' | 'warning' | 'fatalerror'; kod: string | null;
  /** ENYK-mezőazonosító (sorfüggő mezőnél eltérhet a `mezo`-tól), a dinamikus lap indexe, a szabály azonosítója. */
  fid?: string; lapIndex?: number; szabaly?: string | null;
  /** 'mezo' mezőellenőrzés, 'szamitas' számított érték eltérése, 'feltetel' feltételes érték, 'lap' lapengedély, 'form' nyomtatványszintű. */
  forras?: 'mezo' | 'szamitas' | 'feltetel' | 'lap' | 'form';
  /** A szabályt hordozó mező (feltételes értéknél eltér a célmezőtől). */
  forrasMezo?: string;
  /** ENYK-only mező (nincs XSD-megfelelője). */
  aux?: boolean;
}

export interface AnykLancLepes {
  nev: string;
  /** Sorindex a láncban; a dinamikus lap külső láncánál a lapon belüli eltolás. */
  sor?: number;
  /** A lánc fejléce ('Chain_X#fej'). */
  fej?: boolean;
  /** Dinamikus lap külső lánca: ennyi láncsor esik egy lappéldányra (sor = lapIndex·laponkent + sor). */
  laponkent?: number;
}

export interface AnykFidHely {
  fid: string; form: string;
  /** Kulcs az adatobjektumban: az XSD-mező kódja, ENYK-only mezőnél maga a fid ('#aux' alatt). */
  kod: string;
  aux: boolean;
  lap: string | null; pid: string | null; dinamikus: boolean;
  lancok: AnykLancLepes[];
  /** Csak az első lappéldány hordozza (dinamikus lapon lévő, láncon kívüli / fejléc mező). */
  csakElsoLap?: boolean;
}

export interface AnykStatisztika {
  szabalyDb: number;
  /** Fordításkor ismeretlen függvény → a szabály nem értékelhető. */
  ismeretlenFuggveny: Record<string, number>;
  /** Az ÁNYK-ban létező, de itt nem támogatott függvények (a szabály eredménye nil = nem jelez hibát). */
  nemTamogatott: Record<string, number>;
  ismeretlenMezo: Record<string, number>;
  /** Az utolsó hívásban kiértékelt szabályok, ebből futási hibás (kivétel) és ismeretlen függvényt érintő. */
  kiertekelt: number; futasiHiba: number; ismeretlentErinto: number;
  futasiHibak: { szabaly: string; uzenet: string }[];
}

/* ============================== BELSŐ TÍPUSOK ============================== */

interface AnykLista { lista: true; matrix: string; hatarolo: string; szurok: { oszlop: number; rel: string; ertek: string }[]; }
type Ertek = null | string | number | boolean | AnykLista;
type Fv = (c: Ktx) => Ertek;

interface Szabaly {
  kulcs: string; id: string | null; fn: Fv; esemeny: string[]; uzenet: string;
  szint: 'error' | 'warning' | 'fatalerror'; kod: string | null;
  fid: string | null; pid: string | null; celKotes: boolean; gyoker: string;
  ismeretlen: string[]; feltetelesErtek: boolean;
}

interface LapInfo {
  pid: string; nev: string; dinamikus: boolean; maxDb: number;
  /** Dinamikus lap külső lánca (XSD) és a lappéldányonkénti sorok száma. */
  lanc: string | null; laponkent: number; lancFej: boolean;
  fidek: string[]; ellenorzes: Szabaly[];
}

interface MezoInfo {
  fid: string; tipus: number; lap: LapInfo; hely: AnykFidHely; vid: string | null;
  datum: boolean; logikai: boolean; alap: string | null; calcZe: boolean;
  sor: number | null; oszlop: number | null; kerekites: number | null;
  ellenorzes: Szabaly[]; szamitas: Szabaly[];
}

interface FormInfo {
  id: string; nev: string; fo: boolean;
  lapok: LapInfo[]; lapPid: Map<string, LapInfo>;
  /*  Kódkereső („lookup") mezők: a nyomtatványon a kód és a megnevezés két külön mező, de
   *  ugyanabba a csoportba tartoznak, és ugyanannak a mátrixnak más-más oszlopát mutatják. */
  lookupok: Map<string, { csoport: string; oszlop: number; matrixok: string[]; megjelenit: number[]; hatarolo: string }>;
  lookupCsoportok: Map<string, string[]>;
  mezok: Map<string, MezoInfo>; vidFid: Map<string, string>;
  ellenorzendo: MezoInfo[]; szamitasSorrend: MezoInfo[];
  formEllenorzes: Szabaly[]; feltetelesSzabalyok: { info: MezoInfo; sz: Szabaly }[];
  valtozok: Map<string, any>; valtozoFv: Map<string, Fv>;
  kodFidek: Map<string, string[]>;
}

/** Egy kiértékelési menet gyorsítótárai (ellenőrzés közben az adat nem változik). */
interface Memo {
  aux: Map<any, Map<string, any>>;
  lapDb: Map<any, Map<string, number>>;
  glob: Map<string, Ertek>;
  gyorsit: boolean;
}

interface Ktx {
  m: AnykSzabalymotor; adat: any; form: FormInfo; pi: number; p: any;
  self: MezoInfo | null; k: number;
  felulir: Map<string, number> | null; formCheck: boolean;
  mindUres: boolean; zerus: boolean; valtozoban: number;
  memo: Memo; mod: 'ellenoriz' | 'szamol';
  jelzesek: { cel: MezoInfo; k: number; jelenlegi: string; szamitott: string }[] | null;
  feltetelesIr: boolean;
}

class AnykFutasiHiba extends Error {}

/* ============================== ÉRTÉKKEZELÉS ============================== */

/** ÁNYK-típusok: 0 nil, 1 szöveg, 2 szám, 4 logikai, 5 lista. */
function tp(v: Ertek): number {
  if (v === null || v === undefined) return 0;
  switch (typeof v) {
    case 'string': return 1;
    case 'number': return 2;
    case 'boolean': return 4;
    default: return 5;
  }
}

const SZAM_RE = /^\s*[+-]?(\d+\.?\d*|\.\d+)([eE][+-]?\d+)?\s*$/;

/** Számszöveg (Java BigDecimal-szerű, lebegőpontos zaj nélkül). */
function szamSzoveg(n: number): string {
  if (!isFinite(n)) return '';
  if (Number.isInteger(n)) return String(n);
  let s = n.toFixed(10);
  s = s.replace(/0+$/, '').replace(/\.$/, '');
  return s === '-0' ? '0' : s;
}

function szoveg(v: Ertek): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'number') return szamSzoveg(v);
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  if (typeof v === 'string') return v;
  return '';
}

/** ExpFactoryLight.createConstant: szöveg → ÁNYK-érték a mező típusa szerint. */
function konstans(s: string | null, tipus: number): Ertek {
  if (s === null || s === undefined || s === '') return tipus === 4 ? false : null;
  if (tipus === 1) return s;
  if (tipus === 4 && (s === 'X' || s === 'x' || s === '1')) return true;
  const l = s.toLowerCase();
  if (l === 'true') return true;
  if (l === 'false') return false;
  if (s.length > 1 && s[0] === '"' && s[s.length - 1] === '"') return s.slice(1, -1);
  if (SZAM_RE.test(s)) return Number(s.trim());
  return s;
}

/** Üres-e a mezőérték (FunctionBodies.isFieldEmpty): a 0 NEM üres, a false üres. */
function uresErtek(v: Ertek): boolean {
  if (v === null || v === undefined) return true;
  if (typeof v === 'boolean') return !v;
  if (typeof v === 'string') return v.length === 0;
  return false;
}

/** baseTypeConversion1: nil → 0, szöveg → konstans (szám, ha az). */
function szamra(v: Ertek): Ertek {
  if (v === null || v === undefined) return 0;
  if (typeof v === 'string') return konstans(v === '' ? '0' : v, -2);
  return v;
}

const EPS = 1e-9;
function szamEgyenlo(a: number, b: number): boolean { return Math.abs(a - b) <= EPS * Math.max(1, Math.abs(a), Math.abs(b)); }
function szamHasonlit(a: number, b: number): number { return szamEgyenlo(a, b) ? 0 : (a < b ? -1 : 1); }

/** FunctionBodies.Tz_round: pozitívnál fél felfelé, negatívnál fél a nulla felé (= floor(x + 0,5)). */
function kerekit(x: number): number { return Math.floor(Number(x.toFixed(9)) + 0.5); }

/** NumericOperations.processArithmAndLogical relációi és logikai műveletei. */
function muvelet(op: string, a: Ertek, b: Ertek): Ertek {
  const sz = typeof a === 'string' || typeof b === 'string';
  if (sz || (a === null && b === null)) {
    if (a !== null && b !== null) {
      if (op === '=') return typeof a === 'string' ? a === szoveg(b) : false;
      if (op === '!=') return typeof a === 'string' ? a !== szoveg(b) : true;
      if (op === '&' || op === '|' || op === '+') return szoveg(a) + szoveg(b);
      return null;
    }
    if (a !== null || b !== null) {
      if (op === '=') return false;
      if (op === '!=') return true;
      return szoveg(a !== null ? a : b);
    }
    if (op === '!=') return false;
    if (op === '=' || op === '<' || op === '>' || op === '<=' || op === '>=') return true;
    return null;
  }
  const szam = typeof a === 'number' || typeof b === 'number';
  if (szam) {
    if (typeof a === 'boolean' || typeof b === 'boolean') throw new AnykFutasiHiba('típuseltérés (logikai ↔ szám)');
    if (a !== null && b !== null) {
      const x = a as number, y = b as number;
      switch (op) {
        case '+': return x + y;
        case '-': return x - y;
        case '*': return x * y;
        case '/': return y === 0 ? null : x / y;
        case '&': return (x & y);
        case '|': return (x | y);
        case '>': return szamHasonlit(x, y) > 0;
        case '<': return szamHasonlit(x, y) < 0;
        case '=': return szamEgyenlo(x, y);
        case '>=': return szamHasonlit(x, y) >= 0;
        case '<=': return szamHasonlit(x, y) <= 0;
        case '!=': return !szamEgyenlo(x, y);
      }
      return null;
    }
    if (op === '=') return false;
    if (op === '!=') return true;
    if (op === '*' || op === '/' || op === '<' || op === '>' || op === '<=' || op === '>=') return null;
    if (a === null && op === '-') return -(b as number);
    return (a !== null ? a : b);
  }
  // logikai (vagy egyik nil)
  if (typeof a === 'boolean' || typeof b === 'boolean') {
    if (a !== null && b !== null) {
      switch (op) {
        case '&': return (a as boolean) && (b as boolean);
        case '|': return (a as boolean) || (b as boolean);
        case '=': return a === b;
        case '!=': return a !== b;
      }
      return null;
    }
    // ÁNYK: & nil → hamis; | esetén a BAL oldali érték (bal nil → nil); = hamis; != igaz
    switch (op) {
      case '&': return false;
      case '|': return a !== null ? a : null;
      case '=': return false;
      case '!=': return true;
    }
    return null;
  }
  return null;
}

/* ─────────────── dátum (ÁNYK: ééééhhnn szám) ─────────────── */

function joDatum(s: string): boolean {
  if (!/^\d{8}$/.test(s)) return false;
  const e = +s.slice(0, 4), h = +s.slice(4, 6), n = +s.slice(6, 8);
  if (h < 1 || h > 12 || n < 1) return false;
  return n <= new Date(Date.UTC(e, h, 0)).getUTCDate();
}
function datumNapok(s: string): number { return Date.UTC(+s.slice(0, 4), +s.slice(4, 6) - 1, +s.slice(6, 8)) / 86400000; }
function napDatum(n: number): number {
  const d = new Date(n * 86400000);
  return d.getUTCFullYear() * 10000 + (d.getUTCMonth() + 1) * 100 + d.getUTCDate();
}

/* ─────────────── azonosító-ellenőrzők (FunctionBodies) ─────────────── */

function cdv8(s: string): boolean {
  const w = [9, 7, 3, 1, 9, 7, 3, 1, 9, 7, 3, 1];
  let o = 0;
  for (let i = 0; i < s.length - 1; i++) o += (s.charCodeAt(i) - 48) * w[i];
  const e = (10 - (o % 10)) % 10;
  const u = +s[s.length - 1];
  return e === u || e + 1 === u;
}
function chkInputIds(t: number, s: string, hossz: number): number {
  if (t === 1 ? s.length === 0 : s === '0') return 0;
  return s.length === hossz && /^\d+$/.test(s) ? 1 : -1;
}

/* ============================== LEKÉPEZÉS (ENYK ↔ adat) ============================== */

const FID_SOR = /^(\d[A-Z](?:\d{4}|XXXX)[A-Z])(\d{4})([A-Z])A$/;

function formNevbol(x: string): string { return String(x || '').replace(/^Form_/, ''); }

/** Az XSD-modell egy formjának mezői: kód → { adattípus, láncút }. */
function xsdIndex(xsdForm: any): Map<string, { adattipus: string; lancok: { nev: string; fej: boolean; elemMax: number | null }[] }> {
  const idx = new Map<string, { adattipus: string; lancok: { nev: string; fej: boolean; elemMax: number | null }[] }>();
  if (!xsdForm) return idx;
  const bejar = (n: any, lancok: { nev: string; fej: boolean; elemMax: number | null }[]) => {
    if (!n) return;
    if (n.tipus === 'mezo') { idx.set(n.kod, { adattipus: n.adattipus, lancok }); return; }
    if (n.tipus === 'lanc') {
      (n.fej || []).forEach((e: any) => bejar(e, [...lancok, { nev: n.nev, fej: true, elemMax: null }]));
      (n.elemek || []).forEach((e: any) => bejar(e, [...lancok, { nev: n.nev, fej: false, elemMax: n.elemMax }]));
      return;
    }
    (n.elemek || []).forEach((e: any) => bejar(e, lancok));
  };
  (xsdForm.elemek || []).forEach((e: any) => bejar(e, []));
  return idx;
}

function vanAdat(o: any): boolean {
  if (!o || typeof o !== 'object') return false;
  for (const k of Object.keys(o)) {
    if (k === '#aux') continue;
    const v = o[k];
    if (v === null || v === undefined || v === '' || v === false) continue;
    if (Array.isArray(v)) { if (v.some((x) => vanAdat(x))) return true; continue; }
    if (typeof v === 'object') { if (vanAdat(v)) return true; continue; }
    return true;
  }
  return false;
}

/* ============================== A MOTOR ============================== */

export class AnykSzabalymotor {
  private formok: FormInfo[] = [];
  private formId = new Map<string, FormInfo>();
  private matrixok = new Map<string, string[]>();
  private stat: AnykStatisztika;
  private futasiHibak = new Map<string, string>();
  private kiertekeltek = new Set<string>();
  private ismeretlenErintett = new Set<string>();

  constructor(enyk: any, xsdModell: any) {
    this.stat = { szabalyDb: 0, ismeretlenFuggveny: {}, nemTamogatott: {}, ismeretlenMezo: {}, kiertekelt: 0, futasiHiba: 0, ismeretlentErinto: 0, futasiHibak: [] };
    for (const [nev, ertekek] of Object.entries<any>(enyk?.matrixok || {})) this.matrixok.set(nev.toLowerCase(), (ertekek || []).map((x: any) => String(x ?? '')));
    const xsdFormok: any[] = xsdModell?.formok || [];
    for (const [id, ef] of Object.entries<any>(enyk?.formok || {})) {
      const xf = xsdFormok.find((f) => f.kod === id || f.nev === 'Form_' + id);
      this.formok.push(this.formEpit(id, ef, xf));
    }
    for (const f of this.formok) this.formId.set(f.id, f);
    // Főform (egyszer létrehozható, a többire hivatkozik: globsum/unique_m) – a melléklapok előbb számolandók.
    const fo = (this.formok.length > 1 ? this.formok.find((f) => enyk.formok[f.id]?.maxDb === 1) : null) || this.formok.find((f) => /A$/.test(f.id)) || this.formok[0];
    if (fo) fo.fo = true;
    for (const f of this.formok) this.forditForm(f, enyk.formok[f.id]);
  }

  /* ─────────────── szerkezet ─────────────── */

  private formEpit(id: string, ef: any, xf: any): FormInfo {
    const f: FormInfo = {
      id, nev: xf?.nev || 'Form_' + id, fo: false, lapok: [], lapPid: new Map(), mezok: new Map(), vidFid: new Map(),
      lookupok: new Map(), lookupCsoportok: new Map(),
      ellenorzendo: [], szamitasSorrend: [], formEllenorzes: [], feltetelesSzabalyok: [], valtozok: new Map(), valtozoFv: new Map(), kodFidek: new Map(),
    };
    const xi = xsdIndex(xf);
    const sorIdx = new Map<string, { sor: number; kod: string }[]>();
    xi.forEach((x, kod) => {
      const m = FID_SOR.exec(kod);
      const bel = x.lancok[x.lancok.length - 1];
      if (m && bel && !bel.fej) {
        const k = m[1] + '|' + m[3];
        if (!sorIdx.has(k)) sorIdx.set(k, []);
        sorIdx.get(k)!.push({ sor: +m[2], kod });
      }
    });
    const metak = ef.metak || {};
    for (const o of ef.oldalak || []) {
      const lap: LapInfo = { pid: String(o.pid), nev: o.nev, dinamikus: !!o.dinamikus, maxDb: o.maxDb || 1, lanc: null, laponkent: 1, lancFej: false, fidek: [], ellenorzes: [] };
      f.lapok.push(lap); f.lapPid.set(lap.pid, lap);
      const fidek: string[] = (o.elemek || []).filter((e: any) => e.t === 'mezo' && e.fid).map((e: any) => String(e.fid));
      lap.fidek = Array.from(new Set(fidek));
      // XSD-hely feloldása (egyenes egyezés vagy sorfüggő ENYK-mező)
      const nyers = new Map<string, { kod: string; lancok: { nev: string; fej: boolean; elemMax: number | null }[]; eltol: number } | null>();
      for (const fid of lap.fidek) {
        const x = xi.get(fid);
        if (x) { nyers.set(fid, { kod: fid, lancok: x.lancok, eltol: 0 }); continue; }
        const m = FID_SOR.exec(fid);
        let talalt: { kod: string; lancok: any[]; eltol: number } | null = null;
        if (m) {
          const n = +m[2];
          const jelolt = (sorIdx.get(m[1] + '|' + m[3]) || []).filter((c) => c.sor <= n).sort((a, b) => b.sor - a.sor)[0];
          if (jelolt) {
            const lx = xi.get(jelolt.kod)!;
            const bel = lx.lancok[lx.lancok.length - 1];
            const eltol = n - jelolt.sor;
            if (bel.elemMax == null || eltol < bel.elemMax) talalt = { kod: jelolt.kod, lancok: lx.lancok, eltol };
          }
        }
        nyers.set(fid, talalt);
      }
      if (lap.dinamikus) {
        for (const h of nyers.values()) if (h && h.lancok.length) { lap.lanc = h.lancok[0].nev; break; }
        if (lap.lanc) {
          let max = 0;
          for (const h of nyers.values()) if (h && h.lancok.length === 1 && !h.lancok[0].fej && h.lancok[0].nev === lap.lanc) max = Math.max(max, h.eltol);
          lap.laponkent = max + 1;
          lap.lancFej = Array.from(nyers.values()).some((h) => !!h && h.lancok.length === 1 && h.lancok[0].fej);
        }
      }
      for (const fid of lap.fidek) {
        const meta = metak[fid] || {};
        const h = nyers.get(fid);
        const x = h ? xi.get(h.kod) : null;
        const hely: AnykFidHely = { fid, form: f.nev, kod: h ? h.kod : fid, aux: !h, lap: lap.nev, pid: lap.pid, dinamikus: lap.dinamikus, lancok: [] };
        if (h) {
          h.lancok.forEach((l, i) => {
            const lepes: AnykLancLepes = { nev: l.nev };
            if (l.fej) { lepes.fej = true; if (i === 0 && lap.dinamikus) hely.csakElsoLap = true; }
            else {
              if (i === 0 && lap.dinamikus && l.nev === lap.lanc) lepes.laponkent = lap.laponkent;
              lepes.sor = i === h.lancok.length - 1 ? h.eltol : 0;
            }
            hely.lancok.push(lepes);
          });
          if (!h.lancok.length && lap.dinamikus) hely.csakElsoLap = true;
        } else if (lap.dinamikus && lap.lanc) {
          hely.lancok.push({ nev: lap.lanc, sor: 0, laponkent: lap.laponkent });
        }
        const tipus = Number(meta.tipus) || 1;
        const info: MezoInfo = {
          fid, tipus, lap, hely, vid: meta.vid || null,
          datum: x?.adattipus === 'datum', logikai: x ? x.adattipus === 'logikai' : tipus === 4,
          alap: meta.alapertek != null ? String(meta.alapertek) : (meta.defaultvalue != null ? String(meta.defaultvalue) : null),
          calcZe: meta.calcZe === undefined ? true : !!meta.calcZe,
          sor: meta.sor != null && meta.sor !== '' ? Number(meta.sor) : null,
          oszlop: meta.oszlop != null && meta.oszlop !== '' ? Number(meta.oszlop) : null,
          kerekites: meta.kerekites != null ? Number(meta.kerekites) : null,
          ellenorzes: [], szamitas: [],
        };
        if (!f.mezok.has(fid)) f.mezok.set(fid, info);
        if (info.vid && !f.vidFid.has(info.vid)) f.vidFid.set(info.vid, fid);
        const kl = f.kodFidek.get(hely.kod) || [];
        kl.push(fid); f.kodFidek.set(hely.kod, kl);
      }
    }
    // Lapon kívüli META-mezők (a 2608-ban nincs ilyen): statikus, példányszintű tárolás.
    for (const [fid, meta] of Object.entries<any>(metak)) {
      if (f.mezok.has(fid)) continue;
      const lap = f.lapok[0];
      if (!lap) break;
      const x = xi.get(fid);
      const hely: AnykFidHely = { fid, form: f.nev, kod: fid, aux: !x, lap: lap.nev, pid: lap.pid, dinamikus: false, lancok: [] };
      f.mezok.set(fid, {
        fid, tipus: Number(meta.tipus) || 1, lap: { ...lap, dinamikus: false }, hely, vid: meta.vid || null, datum: x?.adattipus === 'datum', logikai: x?.adattipus === 'logikai',
        alap: meta.alapertek != null ? String(meta.alapertek) : null, calcZe: meta.calcZe === undefined ? true : !!meta.calcZe,
        sor: meta.sor ? Number(meta.sor) : null, oszlop: meta.oszlop ? Number(meta.oszlop) : null, kerekites: null, ellenorzes: [], szamitas: [],
      });
      if (meta.vid && !f.vidFid.has(meta.vid)) f.vidFid.set(meta.vid, fid);
    }
    return f;
  }

  /* ─────────────── fordítás ─────────────── */

  private forditForm(f: FormInfo, ef: any) {
    for (const v of ef.valtozok || []) f.valtozok.set(v.nev, v.kif);
    const uj = (r: any, fid: string | null, pid: string | null, i: number, hol: string): Szabaly => {
      this.stat.szabalyDb++;
      const kulcs = `${f.id}:${hol}:${r.id ?? i}`;
      const sz: Szabaly = {
        kulcs, id: r.id ?? null, fn: () => null, esemeny: r.esemeny || [], uzenet: r.uzenet || '',
        szint: (r.szint === 'warning' || r.szint === 'fatalerror') ? r.szint : 'error', kod: r.kod || null,
        fid, pid, celKotes: r.celKotes !== false, gyoker: Array.isArray(r.kif) ? String(r.kif[0]).toLowerCase() : '',
        ismeretlen: [], feltetelesErtek: false,
      };
      if (!r.kif || (r.kif as any).hiba !== undefined) {
        sz.ismeretlen.push('(értelmezési hiba)');
        this.stat.ismeretlenFuggveny['(értelmezési hiba)'] = (this.stat.ismeretlenFuggveny['(értelmezési hiba)'] || 0) + 1;
        return sz;
      }
      sz.fn = this.ford(r.kif, f, sz);
      return sz;
    };
    for (const [fid, lista] of Object.entries<any[]>(ef.mezoSzabalyok || {})) {
      const info = f.mezok.get(fid);
      (lista || []).forEach((r, i) => {
        const ev: string[] = r.esemeny || [];
        const sz = uj(r, fid, info?.lap.pid ?? null, i, 'f' + fid + '#' + i);
        if (!info) return;
        if (ev.includes('lookup_create')) this.lookupRogzit(f, fid, r.kif);
        if (ev.includes('field_check')) {
          info.ellenorzes.push(sz);
          if (sz.feltetelesErtek) f.feltetelesSzabalyok.push({ info, sz });
        } else if (ev.includes('field_calc') && !ev.includes('multi_form_load') && sz.celKotes && sz.gyoker !== 'callgui') {
          info.szamitas.push(sz);
        }
      });
    }
    (ef.formSzabalyok || []).forEach((r: any, i: number) => { const sz = uj(r, null, null, i, 'form#' + i); if ((r.esemeny || []).includes('form_check')) f.formEllenorzes.push(sz); });
    for (const [pid, lista] of Object.entries<any[]>(ef.lapSzabalyok || {})) {
      const lap = f.lapPid.get(String(pid));
      (lista || []).forEach((r, i) => { const sz = uj(r, null, String(pid), i, 'lap' + pid + '#' + i); if (lap && (r.esemeny || []).includes('page_check')) lap.ellenorzes.push(sz); });
    }
    (ef.eloSzabalyok || []).forEach((r: any, i: number) => uj(r, null, null, i, 'elo#' + i));
    (ef.utoSzabalyok || []).forEach((r: any, i: number) => uj(r, null, null, i, 'uto#' + i));
    // ellenőrizendő mezők lapsorrendben
    for (const lap of f.lapok) for (const fid of lap.fidek) { const i = f.mezok.get(fid); if (i && (i.ellenorzes.length || i.szamitas.length)) f.ellenorzendo.push(i); }
    f.szamitasSorrend = this.szamitasiSorrend(f, ef);
  }

  /*  A `lookup_create` szabályból kiolvassuk, MELYIK mátrix MELYIK oszlopát mutatja a mező, és
   *  melyik csoportba tartozik. Alakja: feltételes_értéklista2(szabad, oszlop, "megjelenítés",
   *  <mátrix_szűrés(…)>, "csoport") – a T201T-nél a TEÁOR-kód az 1., a megnevezés a 2. oszlop,
   *  mindkettő az „A01_5" csoportban. */
  private lookupRogzit(f: FormInfo, fid: string, kif: any) {
    let oszlop = 0; let csoport = ''; const matrixok: string[] = []; let megjelenit: number[] = []; let hatarolo = '';
    const bejar = (n: any) => {
      if (!Array.isArray(n)) return;
      const nev = typeof n[0] === 'string' ? n[0].toLowerCase() : '';
      if (nev.startsWith('feltételes_értéklista')) {
        if (typeof n[2] === 'number') oszlop = n[2];
        /*  A 3. argumentum a MEGJELENÍTÉS oszlopai („1,77" = a kód és mellette a megnevezés).
         *  Ebből tudjuk, mit írjunk a választólista tételei mellé. */
        if (n[3] && typeof n[3] === 'object' && typeof n[3].s === 'string') {
          megjelenit = n[3].s.split(',').map((x: string) => Number(x.trim())).filter((x: number) => x > 0);
        }
        const utolso = n[n.length - 1];
        if (utolso && typeof utolso === 'object' && typeof utolso.s === 'string') csoport = utolso.s;
      }
      if (nev.startsWith('mátrix') && n[1] && typeof n[1] === 'object' && typeof n[1].s === 'string') {
        matrixok.push(n[1].s);
        //  Az oszlopok elválasztója a mátrixhoz tartozik (a sablon sajátjaiban „=”, az általános
        //  irányítószám-mátrixban „;”) – eddig fixen „=”-vel bontottunk.
        if (!hatarolo && n[2] && typeof n[2] === 'object' && typeof n[2].s === 'string' && n[2].s) hatarolo = n[2].s;
      }
      n.forEach((x: any) => bejar(x));
    };
    bejar(kif);
    if (!oszlop || !matrixok.length || f.lookupok.has(fid)) return;
    f.lookupok.set(fid, { csoport, oszlop, matrixok: [...new Set(matrixok)], megjelenit, hatarolo: hatarolo || '=' });
    if (!csoport) return;
    const l = f.lookupCsoportok.get(csoport) || [];
    l.push(fid); f.lookupCsoportok.set(csoport, l);
  }

  /* ─────────────── munkanaptár ─────────────── */
  /*  A hétvégék és a munkaszüneti napok (Mt. 102. § (1): jan. 1., márc. 15., nagypéntek,
   *  húsvéthétfő, máj. 1., pünkösdhétfő, aug. 20., okt. 23., nov. 1., dec. 24–26.) évről évre
   *  kiszámíthatók, a mozgó ünnepekkel együtt. Az ÉVENKÉNTI MUNKANAP-ÁTHELYEZÉSEKET (NGM-
   *  rendelet: „szombat munkanap, péntek pihenőnap") viszont csak adatból lehet tudni – azokat
   *  a gazda tölti be (`munkanaptarBeallit`), ugyanabból a forrásból, amiből a bérszámfejtés
   *  dolgozik (SULINFORM `holiday` tábla országos sorai). Adat híján a naptár a hétvégéket és
   *  az ünnepeket ismeri, az áthelyezéseket nem. */
  private naptarFelulir = new Map<string, boolean>();
  private unnepTar = new Map<number, Set<string>>();

  /** Munkanap-áthelyezések betöltése: 'ÉÉÉÉ-HH-NN' → munkanap-e. */
  munkanaptarBeallit(felulirasok: Record<string, boolean> | null | undefined) {
    this.naptarFelulir = new Map(Object.entries(felulirasok || {}).map(([k, v]) => [k, !!v]));
  }

  private unnepek(ev: number): Set<string> {
    const van = this.unnepTar.get(ev);
    if (van) return van;
    // húsvétvasárnap (Meeus/Jones/Butcher)
    const a = ev % 19, b = Math.floor(ev / 100), c = ev % 100, d = Math.floor(b / 4), e = b % 4;
    const f = Math.floor((b + 8) / 25), g = Math.floor((b - f + 1) / 3), h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4), k = c % 4, l = (32 + 2 * e + 2 * i - h - k) % 7, mm = Math.floor((a + 11 * h + 22 * l) / 451);
    const ho = Math.floor((h + l - 7 * mm + 114) / 31), nap = ((h + l - 7 * mm + 114) % 31) + 1;
    const husvet = Date.UTC(ev, ho - 1, nap);
    const iso = (ms: number) => new Date(ms).toISOString().slice(0, 10);
    const t = new Set<string>([
      ...['01-01', '03-15', '05-01', '08-20', '10-23', '11-01', '12-24', '12-25', '12-26'].map((x) => `${ev}-${x}`),
      iso(husvet - 2 * 86400000), iso(husvet + 86400000), iso(husvet + 50 * 86400000),
    ]);
    this.unnepTar.set(ev, t);
    return t;
  }

  /** @internal Munkanap-e a megadott nap (ÉÉÉÉHHNN). */
  _munkanapE(nyolcJegy: string): boolean | null {
    const ev = Number(nyolcJegy.slice(0, 4)), ho = Number(nyolcJegy.slice(4, 6)), nap = Number(nyolcJegy.slice(6, 8));
    if (!(ev > 1900 && ho >= 1 && ho <= 12 && nap >= 1 && nap <= 31)) return null;
    const d = new Date(Date.UTC(ev, ho - 1, nap));
    if (d.getUTCMonth() + 1 !== ho || d.getUTCDate() !== nap) return null;     // nem létező nap
    const iso = `${nyolcJegy.slice(0, 4)}-${nyolcJegy.slice(4, 6)}-${nyolcJegy.slice(6, 8)}`;
    if (this.naptarFelulir.has(iso)) return this.naptarFelulir.get(iso)!;
    const hetnap = d.getUTCDay();
    if (hetnap === 0 || hetnap === 6) return false;
    return !this.unnepek(ev).has(iso);
  }

  /**
   *  A KÓDKERESŐ MEZŐK VÁLASZTÉKA: fid → [{ érték, felirat }].
   *
   *  Ezek a listák nem az XSD-ben és nem is a mező META-jában vannak, hanem a `lookup_create`
   *  szabályban – ezért a felület eddig NEM kínált hozzájuk lenyílót (pl. az ÚJEGYKE
   *  „képviselet jogcíme" mezőjénél), pedig a sablon pontosan megmondja, melyik mátrix melyik
   *  oszlopát kell felkínálni, és mit írjunk mellé.
   */
  lookupListak(form?: string): Map<string, { ertek: string; cim: string | null }[]> {
    const ki = new Map<string, { ertek: string; cim: string | null }[]>();
    for (const f of this.formok) {
      if (form && f.id !== formNevbol(form)) continue;
      for (const [fid, l] of f.lookupok) {
        const cimOszlopok = (l.megjelenit || []).filter((x) => x !== l.oszlop);
        const lista: { ertek: string; cim: string | null }[] = [];
        const latott = new Set<string>();
        for (const mx of l.matrixok) {
          for (const sor of this._matrix(mx) || []) {
            const o = sor.split(l.hatarolo);
            const e = szoveg(o[l.oszlop - 1]);
            if (!e || latott.has(e)) continue;
            latott.add(e);
            const cim = cimOszlopok.map((c) => szoveg(o[c - 1])).filter(Boolean).join(' – ');
            lista.push({ ertek: e, cim: cim || null });
          }
        }
        if (lista.length) ki.set(fid, lista);
      }
    }
    return ki;
  }

  /*  KÓD ↔ MEGNEVEZÉS. Egy lookup-csoport mezői ugyanannak a mátrixnak más-más oszlopát mutatják
   *  (TEÁOR-kód + megnevezés; irányítószám + település). Ha a felhasználó az egyiket kitölti, a
   *  többit a mátrix ugyanazon sorából töltjük.
   *
   *  MELYIK A KULCS? Eddig mindig a legkisebb oszlop – csakhogy az irányítószám-mátrixban a
   *  település az 1. oszlop, így az irányítószám beírására semmi nem történt. Ma az a kulcs,
   *  amelyiket a legutóbbi futás óta MÓDOSÍTOTTÁK (erre emlékszünk); első betöltéskor az, amelyik
   *  egyértelműen egy sort azonosít. Ha a mezők már összeillenek (van olyan sor, amelyik mindet
   *  tartalmazza), nem nyúlunk semmihez. Ha a kulcs nem egyértelmű (pl. „Budapest”), nem találgatunk.
   *  Számított mezőhöz nem nyúlunk (annak a saját szabálya a gazdája). */
  private lookupElozo = new Map<string, string>();
  private lookupSorTar = new Map<string, string[][]>();
  private lookupSorok(l: { matrixok: string[]; hatarolo: string }): string[][] {
    const k = l.matrixok.join('|') + '§' + l.hatarolo;
    let t = this.lookupSorTar.get(k);
    if (!t) {
      t = [];
      for (const mx of l.matrixok) for (const sor of this._matrix(mx) || []) t.push(sor.split(l.hatarolo).map((x) => szoveg(x)));
      this.lookupSorTar.set(k, t);
    }
    return t;
  }
  private lookupToltes(c0: Ktx, p: any, f: FormInfo): number {
    let n = 0;
    for (const [csoport, fidek] of f.lookupCsoportok) {
      if (fidek.length < 2) continue;
      const rend = fidek.map((fid) => ({ fid, l: f.lookupok.get(fid)!, info: f.mezok.get(fid) }))
        .filter((x) => x.l && x.info).sort((a, b) => a.l.oszlop - b.l.oszlop);
      if (rend.length < 2) continue;
      const sorok = this.lookupSorok(rend[0].l);
      if (!sorok.length) continue;
      const oszl = (sor: string[], i: number) => sor[rend[i].l.oszlop - 1] ?? '';
      const db = this.lapDbKtx(c0, rend[0].info!.lap);
      for (let k = 0; k < db; k++) {
        const ertekek = rend.map((t) => szoveg(this.nyersKtx(this.ktx(c0.adat, f, c0.pi, p, t.info!, k, c0.memo, 'szamol'), t.info!, k)));
        const emlek = (i: number) => `${f.id}|${csoport}|${k}|${rend[i].fid}`;
        const emlekszik = () => rend.forEach((_, i) => this.lookupElozo.set(emlek(i), szoveg(this.nyersKtx(this.ktx(c0.adat, f, c0.pi, p, rend[i].info!, k, c0.memo, 'szamol'), rend[i].info!, k))));
        const kitoltott = ertekek.map((e, i) => (e ? i : -1)).filter((i) => i >= 0);
        const osszeillik = kitoltott.length === rend.length && sorok.some((sor) => kitoltott.every((i) => oszl(sor, i) === ertekek[i]));
        if (!kitoltott.length || osszeillik) { emlekszik(); continue; }
        const valtozott = kitoltott.filter((i) => this.lookupElozo.get(emlek(i)) !== ertekek[i]);
        let sor: string[] | null = null; let kulcs = -1;
        for (const i of (valtozott.length ? valtozott : kitoltott)) {
          const t = sorok.filter((x) => oszl(x, i) === ertekek[i]);
          //  Egyértelmű: egy sor, vagy több, de a többi oszlopban mind ugyanaz.
          if (t.length && t.every((x) => rend.every((_, j) => j === i || oszl(x, j) === oszl(t[0], j)))) { sor = t[0]; kulcs = i; break; }
        }
        if (sor) {
          for (let j = 0; j < rend.length; j++) {
            const t = rend[j];
            if (j === kulcs || t.info!.szamitas.length) continue;   // van saját képlete – nem nyúlunk hozzá
            const uj = oszl(sor, j);
            if (!uj || ertekek[j] === uj) continue;
            if (this.ir(p, t.info!, k, uj)) n++;
          }
        }
        emlekszik();
      }
    }
    return n;
  }

  /** A számított mezők függőségi (topologikus) sorrendje formon belül; körnél a sablon sorrendje. */
  private szamitasiSorrend(f: FormInfo, ef: any): MezoInfo[] {
    const cel = Array.from(f.mezok.values()).filter((i) => i.szamitas.length);
    const celSet = new Set(cel.map((i) => i.fid));
    const fugg = new Map<string, Set<string>>();
    for (const i of cel) {
      const s = new Set<string>();
      for (const r of (ef.mezoSzabalyok || {})[i.fid] || []) {
        if (!(r.esemeny || []).includes('field_calc')) continue;
        const bejar = (n: any) => {
          if (!Array.isArray(n)) return;
          const fn = String(n[0]).toLowerCase();
          const a = n[1] && typeof n[1] === 'object' && !Array.isArray(n[1]) ? n[1].s : undefined;
          if ((fn === 'field' || fn === 'akt_din_érték') && a) s.add(a);
          if ((fn === 'dsum' || fn === 'dprev') && a && f.vidFid.has(a)) s.add(f.vidFid.get(a)!);
          if (fn === 'globsum' || fn === 'compare_am' || fn === 'unique_m') return;
          n.slice(1).forEach(bejar);
        };
        bejar(r.kif);
      }
      s.delete(i.fid);
      fugg.set(i.fid, new Set(Array.from(s).filter((x) => celSet.has(x))));
    }
    const kesz = new Set<string>();
    const out: MezoInfo[] = [];
    const folyamatban = new Set<string>();
    const latogat = (i: MezoInfo) => {
      if (kesz.has(i.fid) || folyamatban.has(i.fid)) return;
      folyamatban.add(i.fid);
      for (const d of fugg.get(i.fid) || []) latogat(f.mezok.get(d)!);
      folyamatban.delete(i.fid);
      kesz.add(i.fid); out.push(i);
    };
    cel.forEach(latogat);
    return out;
  }

  private ford(n: any, f: FormInfo, sz: Szabaly): Fv {
    if (typeof n === 'number') return () => n;
    if (n && typeof n === 'object' && !Array.isArray(n)) {
      if (typeof n.s === 'string') { const s = n.s; return () => s; }
      sz.ismeretlen.push('(értelmezési hiba)');
      return () => null;
    }
    if (typeof n === 'string') {
      const l = n.toLowerCase();
      if (l === 'nil') return () => null;
      if (l === 'true') return () => true;
      if (l === 'false') return () => false;
      if (l === 'zérus') return (c) => { c.zerus = true; return 0; };
      if (f.valtozok.has(n)) return (c) => this.valtozo(c, n);
      if (SZAM_RE.test(n)) { const v = Number(n); return () => v; }
      this.stat.ismeretlenFuggveny['(azonosító) ' + n] = (this.stat.ismeretlenFuggveny['(azonosító) ' + n] || 0) + 1;
      sz.ismeretlen.push(n);
      return () => null;
    }
    if (!Array.isArray(n) || !n.length) return () => null;
    const nev = String(n[0]).toLowerCase();
    const args = n.slice(1);
    const gyarto = FUGGVENYEK[nev];
    if (!gyarto) {
      this.stat.ismeretlenFuggveny[nev] = (this.stat.ismeretlenFuggveny[nev] || 0) + 1;
      sz.ismeretlen.push(nev);
      args.forEach((a: any) => this.ford(a, f, sz));
      return () => null;
    }
    if (nev === 'feltételes_érték' || nev === 'feltételes_érték2') sz.feltetelesErtek = true;
    if (NEM_TAMOGATOTT.has(nev)) this.stat.nemTamogatott[nev] = (this.stat.nemTamogatott[nev] || 0) + 1;
    return gyarto(args, { m: this, f, sz, ford: (x: any) => this.ford(x, f, sz), fordFormban: (id: string, x: any) => this.ford(x, this._form(id) || f, sz) });
  }

  private valtozo(c: Ktx, nev: string): Ertek {
    const f = c.form;
    let fn = f.valtozoFv.get(nev);
    if (!fn) {
      const sz: Szabaly = { kulcs: f.id + ':var:' + nev, id: null, fn: () => null, esemeny: [], uzenet: '', szint: 'error', kod: null, fid: null, pid: null, celKotes: true, gyoker: '', ismeretlen: [], feltetelesErtek: false };
      f.valtozoFv.set(nev, () => null);
      fn = this.ford(f.valtozok.get(nev), f, sz);
      f.valtozoFv.set(nev, fn);
    }
    c.valtozoban++;
    try { return fn(c); } finally { c.valtozoban--; }
  }

  /* ─────────────── adat-hozzáférés ─────────────── */

  /** @internal */ _mezo(f: FormInfo, fid: string): MezoInfo | undefined { return f.mezok.get(fid); }
  /** @internal */ _form(id: string): FormInfo | undefined { return this.formId.get(formNevbol(id)); }
  /** @internal */ _matrix(nev: string): string[] | undefined { return this.matrixok.get(String(nev).toLowerCase()); }
  /** @internal */ _ismeretlenMezo(fid: string) { this.stat.ismeretlenMezo[fid] = (this.stat.ismeretlenMezo[fid] || 0) + 1; }

  private peldanyok(adat: any, f: FormInfo): any[] {
    const l = adat && adat[f.nev];
    return Array.isArray(l) ? l : [];
  }

  /** A mező tárolóobjektuma a k-adik lappéldányon (írásnál létrehozza a hiányzó belső láncsort). */
  private tarolo(p: any, hely: AnykFidHely, k: number, letrehoz: boolean): any {
    if (!p) return null;
    if (hely.csakElsoLap && k > 0) return null;
    let o = p;
    for (const l of hely.lancok) {
      if (l.fej) {
        const kulcs = l.nev + '#fej';
        if (!o[kulcs] || typeof o[kulcs] !== 'object') { if (!letrehoz) return null; o[kulcs] = {}; }
        o = o[kulcs];
        continue;
      }
      const idx = (l.laponkent ? k * l.laponkent : 0) + (l.sor || 0);
      let arr = o[l.nev];
      if (!Array.isArray(arr)) { if (!letrehoz) return null; arr = o[l.nev] = []; }
      if (!arr[idx] || typeof arr[idx] !== 'object') {
        if (!letrehoz) return null;
        while (arr.length <= idx) arr.push({});
        if (!arr[idx] || typeof arr[idx] !== 'object') arr[idx] = {};
      }
      o = arr[idx];
    }
    if (hely.aux) {
      if (!o['#aux'] || typeof o['#aux'] !== 'object') { if (!letrehoz) return null; o['#aux'] = {}; }
      return o['#aux'];
    }
    return o;
  }

  /** Tárolt érték → ÁNYK-szöveg (dátum ééééhhnn, logikai true/false). */
  private nyersbol(v: any, info: MezoInfo): string | null {
    if (v === null || v === undefined || v === '') return null;
    if (v === true) return 'true';
    if (v === false) return info.tipus === 4 ? 'false' : null;
    const s = String(v);
    if (info.datum && /^\d{4}-\d{2}-\d{2}$/.test(s)) return s.replace(/-/g, '');
    return s;
  }

  /** ÁNYK-érték → tárolandó adat (undefined = törlés). */
  private irando(v: Ertek, info: MezoInfo): any {
    if (v === null || v === undefined || v === '' || typeof v === 'object') return undefined;
    if (typeof v === 'number' && info.kerekites != null && isFinite(v)) v = Number(v.toFixed(info.kerekites));
    if (info.hely.aux) return szoveg(v);
    if (info.logikai) return (v === true || /^(true|x|1)$/i.test(String(v))) ? true : undefined;
    if (v === false) return undefined;
    const s = szoveg(v);
    if (info.datum && /^\d{8}$/.test(s)) return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`;
    return s;
  }

  /** A dinamikus lap példányszáma (a lánc sorai alapján). */
  lapDbKtx(c: Ktx, lap: LapInfo): number {
    if (!lap.dinamikus) return 1;
    if (!lap.lanc) return 0;
    let t = c.memo.lapDb.get(c.p);
    if (!t) { t = new Map(); c.memo.lapDb.set(c.p, t); }
    const van = t.get(lap.pid);
    if (van !== undefined) return van;
    const sorok = c.p && Array.isArray(c.p[lap.lanc]) ? c.p[lap.lanc] : [];
    let n = Math.ceil(sorok.length / Math.max(1, lap.laponkent));
    if (!n && lap.lancFej && vanAdat(c.p?.[lap.lanc + '#fej'])) n = 1;
    t.set(lap.pid, n);
    return n;
  }

  /** A mező nyers (ÁNYK-szöveg) értéke a k-adik lappéldányon. Ellenőrzéskor az ENYK-only számított mezők frissen számolódnak. */
  nyersKtx(c: Ktx, info: MezoInfo, k: number): string | null {
    if (info.hely.aux && info.szamitas.length && c.mod === 'ellenoriz') {
      let t = c.memo.aux.get(c.p);
      if (!t) { t = new Map(); c.memo.aux.set(c.p, t); }
      const kulcs = info.fid + '@' + k;
      if (t.has(kulcs)) {
        const v = t.get(kulcs);
        if (v !== FOLYAMATBAN) return v;
      } else {
        t.set(kulcs, FOLYAMATBAN);
        let ered: string | null = null;
        try {
          if (k < this.lapDbKtx(c, info.lap)) {
            const c2 = this.ktx(c.adat, c.form, c.pi, c.p, info, k, c.memo, c.mod);
            const v = this.szamitErtek(c2, info);
            ered = v === undefined ? this.nyersTarolt(c, info, k) : (v === null || v === '' ? null : szoveg(v as Ertek));
          }
        } finally { t.set(kulcs, ered); }
        return ered;
      }
    }
    return this.nyersTarolt(c, info, k);
  }

  private nyersTarolt(c: Ktx, info: MezoInfo, k: number): string | null {
    const o = this.tarolo(c.p, info.hely, k, false);
    const v = o ? this.nyersbol(o[info.hely.aux ? info.fid : info.hely.kod], info) : null;
    return v === null ? info.alap : v;
  }

  /** A mező értéke az aktuális környezetben (field): dinamikus mezőnél az aktuális / felülírt / utolsó lappéldányon. */
  mezoErtekKtx(c: Ktx, info: MezoInfo): Ertek {
    const k = this.indexKtx(c, info);
    const v = konstans(k < 0 ? info.alap : this.nyersKtx(c, info, k), info.tipus);
    if (!c.valtozoban && !uresErtek(v)) c.mindUres = false;
    return v;
  }

  indexKtx(c: Ktx, info: MezoInfo): number {
    if (!info.lap.dinamikus) return 0;
    if (c.felulir && c.felulir.has(info.lap.pid)) return c.felulir.get(info.lap.pid)!;
    if (c.formCheck) return this.lapDbKtx(c, info.lap) - 1;
    return c.k;
  }

  private ktx(adat: any, f: FormInfo, pi: number, p: any, self: MezoInfo | null, k: number, memo: Memo, mod: 'ellenoriz' | 'szamol'): Ktx {
    return { m: this, adat, form: f, pi, p, self, k, felulir: null, formCheck: false, mindUres: true, zerus: false, valtozoban: 0, memo, mod, jelzesek: null, feltetelesIr: false };
  }
  /** @internal */ _ktx(c: Ktx, f: FormInfo, pi: number, p: any): Ktx { return this.ktx(c.adat, f, pi, p, null, 0, c.memo, c.mod); }
  /** @internal */ _peldanyok(adat: any, f: FormInfo): any[] { return this.peldanyok(adat, f); }

  private ujMemo(gyorsit: boolean): Memo { return { aux: new Map(), lapDb: new Map(), glob: new Map(), gyorsit }; }

  /** Egy szabály kiértékelése hibatűrően (kivétel → nil, számlálva). */
  private kiertekel(c: Ktx, sz: Szabaly): Ertek {
    c.mindUres = true; c.zerus = false;
    this.kiertekeltek.add(sz.kulcs);
    if (sz.ismeretlen.length) this.ismeretlenErintett.add(sz.kulcs);
    try {
      return sz.fn(c);
    } catch (e: any) {
      if (!this.futasiHibak.has(sz.kulcs)) this.futasiHibak.set(sz.kulcs, String(e?.message || e));
      return null;
    }
  }

  /**
   *  A mező számított értéke (utolsó célkötött szabály); undefined, ha nincs számítás – vagy ha a
   *  szabály nem értékelhető ki.
   *
   *  NEM ÉRTÉKELHETŐ SZABÁLY. Ha a képletben olyan ÁNYK-függvény szerepel, amit a motor nem
   *  ismer, a fordítás nil-t ad vissza – ezt a motor „ürítsd ki a mezőt” parancsnak vette, és a
   *  mezőt számítottként le is zárta. Egy hiányzó függvény így elvette a felhasználótól a mezőt,
   *  sőt a beírt adatot is törölte. Ilyenkor helyesebb NEM nyúlni a mezőhöz: a nyomtatvány
   *  kitölthető marad, a beküldött XML-t pedig úgyis az XSD-ből építjük.
   */
  private szamitErtek(c: Ktx, info: MezoInfo): Ertek | undefined {
    let v: Ertek | undefined = undefined;
    this.utolsoSzamito = null;
    for (const sz of info.szamitas) {
      if (sz.ismeretlen.length) continue;
      const e = this.kiertekel(c, sz);
      if (e === NINCS) continue;            // a szabály most nem mond semmit a mezőről
      v = e; this.utolsoSzamito = sz;
    }
    return v;
  }
  /** A legutóbb értéket adó szabály – ebből lesz a lezárt mező MAGYARÁZATA a felületen. */
  private utolsoSzamito: Szabaly | null = null;

  /* ─────────────── lapengedély ─────────────── */

  private lapEngedely(c0: Ktx, lap: LapInfo): { nyithato: boolean; sz?: Szabaly } {
    for (const sz of lap.ellenorzes) {
      const c = this.ktx(c0.adat, c0.form, c0.pi, c0.p, null, 0, c0.memo, c0.mod);
      if (this.kiertekel(c, sz) === false) return { nyithato: false, sz };
    }
    return { nyithato: true };
  }

  private lapnakVanAdata(c: Ktx, lap: LapInfo, k: number): boolean {
    for (const fid of lap.fidek) {
      const info = c.form.mezok.get(fid);
      if (!info || info.hely.aux || info.szamitas.length) continue;
      const o = this.tarolo(c.p, info.hely, k, false);
      if (o && this.nyersbol(o[info.hely.kod], info) !== null) return true;
    }
    return false;
  }

  /* ─────────────── mezőellenőrzés ─────────────── */

  private utEpit(hely: AnykFidHely, k: number): string {
    const r: string[] = [];
    for (const l of hely.lancok) {
      if (l.fej) r.push(l.nev + '#fej');
      else r.push(`${l.nev}[${(l.laponkent ? k * l.laponkent : 0) + (l.sor || 0)}]`);
    }
    return r.join('/');
  }

  private hiba(c: Ktx, info: MezoInfo | null, k: number, sz: Szabaly | null, forras: AnykHiba['forras'], uzenet?: string, szint?: AnykHiba['szint']): AnykHiba {
    const h: AnykHiba = {
      form: c.form.nev, peldany: c.pi, ut: info ? this.utEpit(info.hely, k) : '', mezo: info ? info.hely.kod : null,
      lap: info ? info.lap.nev : null, uzenet: uzenet ?? (sz ? sz.uzenet : ''), szint: szint ?? (sz ? sz.szint : 'error'), kod: sz ? sz.kod : null,
      szabaly: sz ? sz.id : null, forras,
    };
    if (info) { h.fid = info.fid; if (info.lap.dinamikus) h.lapIndex = k; if (info.hely.aux) h.aux = true; }
    return h;
  }

  private mezoEllenorzes(c0: Ktx, info: MezoInfo, k: number, hibak: AnykHiba[], szamitasElteres: boolean, osszes = false) {
    const c = this.ktx(c0.adat, c0.form, c0.pi, c0.p, info, k, c0.memo, 'ellenoriz');
    let volt = false;
    for (const sz of info.ellenorzes) {
      c.jelzesek = sz.feltetelesErtek ? [] : null;
      const v = this.kiertekel(c, sz);
      if (c.jelzesek && c.jelzesek.length) {
        for (const j of c.jelzesek) {
          const h = this.hiba(c, j.cel, j.k, sz, 'feltetel',
            `${sz.uzenet ? sz.uzenet + ' ' : ''}(A program szerint a helyes érték: „${j.szamitott || 'üres'}”, a mezőben: „${j.jelenlegi || 'üres'}”.)`, 'warning');
          h.forrasMezo = info.fid;
          hibak.push(h);
        }
      }
      c.jelzesek = null;
      if (v === false) { hibak.push(this.hiba(c, info, k, sz, 'mezo')); volt = true; if (!osszes) break; }
    }
    // Számított mező: a tárolt érték egyezzen a számítottal (ÁNYK „A program szerint a helyes érték …”).
    if (!volt && szamitasElteres && info.szamitas.length && !info.hely.aux) {
      const cs = this.ktx(c0.adat, c0.form, c0.pi, c0.p, info, k, c0.memo, 'ellenoriz');
      const v = this.szamitErtek(cs, info);
      if (v !== undefined) {
        const o = this.tarolo(c0.p, info.hely, k, false);
        const norm = (x: any) => { const e = konstans(this.nyersbol(x, info), info.tipus); return e === false ? '' : szoveg(e); };
        const a = norm(this.irando(v as Ertek, info));
        const b = norm(o ? o[info.hely.kod] : undefined);
        const nullaUres = (a === '' && b === '0') || (a === '0' && b === '');
        if (a !== b && !nullaUres) {
          const sz = info.szamitas[info.szamitas.length - 1];
          hibak.push(this.hiba(c, info, k, sz, 'szamitas',
            `${sz.uzenet ? sz.uzenet + ' ' : ''}A program szerint a helyes érték „${a || 'üres'}”, a mezőben lévő érték „${b || 'üres'}”. (Futtassa a számítást.)`,
            sz.uzenet ? sz.szint : 'warning'));
        }
      }
    }
  }

  /* ============================== NYILVÁNOS FELÜLET ============================== */

  /**
   *  Teljes ellenőrzés: lapengedélyek, mezőellenőrzések (minden létező példány/lappéldány), nyomtatványszintű
   *  szabályok. Soha nem dob. Opciók: szamitasElteres (alap: igen) – a tárolt és a számított érték eltérése;
   *  osszesHiba (alap: nem) – mezőnként MINDEN hibás szabály (az ÁNYK az elsőnél megáll).
   */
  ellenoriz(adat: any, opciok: { szamitasElteres?: boolean; osszesHiba?: boolean } = {}): AnykHiba[] {
    this.futasKezd();
    const hibak: AnykHiba[] = [];
    const memo = this.ujMemo(true);
    const elteres = opciok.szamitasElteres !== false;
    for (const f of this.formok) {
      const peld = this.peldanyok(adat, f);
      peld.forEach((p0, pi) => {
        const p = p0 && typeof p0 === 'object' ? p0 : {};
        try {
          const c = this.ktx(adat, f, pi, p, null, 0, memo, 'ellenoriz');
          const tiltott = new Set<string>();
          for (const lap of f.lapok) {
            const e = this.lapEngedely(c, lap);
            if (e.nyithato) continue;
            tiltott.add(lap.pid);
            const n = lap.dinamikus ? this.lapDbKtx(c, lap) : 1;
            for (let k = 0; k < n; k++) {
              if (this.lapnakVanAdata(c, lap, k)) {
                const h = this.hiba(c, null, k, e.sz!, 'lap');
                h.lap = lap.nev; if (lap.dinamikus) { h.lapIndex = k; h.ut = lap.lanc ? `${lap.lanc}[${k * lap.laponkent}]` : ''; }
                hibak.push(h);
                break;
              }
            }
          }
          for (const info of f.ellenorzendo) {
            if (tiltott.has(info.lap.pid)) continue;
            const n = this.lapDbKtx(c, info.lap);
            for (let k = 0; k < n; k++) this.mezoEllenorzes(c, info, k, hibak, elteres, !!opciok.osszesHiba);
          }
          for (const sz of f.formEllenorzes) {
            const cf = this.ktx(adat, f, pi, p, null, 0, memo, 'ellenoriz');
            cf.formCheck = true;
            if (this.kiertekel(cf, sz) === false) hibak.push(this.hiba(cf, null, 0, sz, 'form'));
          }
        } catch (e: any) {
          this.futasiHibak.set(`${f.id}:peldany:${pi}`, String(e?.message || e));
        }
      });
    }
    this.futasZar();
    return hibak;
  }

  /**
   *  Egy mező azonnali ellenőrzése. `fid`: az XSD-mező kódja (a `ut` szerinti láncsorban) vagy
   *  közvetlenül az ENYK-fid. Ugyanazt adja erre a mezőre, mint az ellenoriz() (forras 'mezo'/'szamitas'/'feltetel').
   */
  mezoEllenoriz(adat: any, form: string, peldany: number, ut: string, fid: string): AnykHiba[] {
    this.futasKezd();
    const hibak: AnykHiba[] = [];
    try {
      const f = this._form(form);
      if (!f) return hibak;
      const p0 = this.peldanyok(adat, f)[peldany];
      const p = p0 && typeof p0 === 'object' ? p0 : {};
      const hely = this.helyUtbol(f, ut || '', fid);
      if (!hely) return hibak;
      const memo = this.ujMemo(true);
      const c = this.ktx(adat, f, peldany, p, null, 0, memo, 'ellenoriz');
      const { info, k } = hely;
      if (k >= this.lapDbKtx(c, info.lap)) return hibak;
      if (!this.lapEngedely(c, info.lap).nyithato) return hibak;
      this.mezoEllenorzes(c, info, k, hibak, true);
    } catch { /* hibatűrő */ }
    this.futasZar();
    return hibak;
  }

  /**
   *  Számítás (field_calc): a számított mezők értékét az adatba írja (XSD-mező a helyére, ENYK-only
   *  mező a '#aux'-ba). Sorrend: melléklapok (M), majd a főlap (A) – függőségi sorrendben, stabil
   *  állapotig (legfeljebb 5 kör). Opcionálisan a feltételes_érték szabályok az ÜRES célmezőt kitöltik.
   *  Visszatér: a megváltozott mezőértékek száma.
   */
  szamol(adat: any, opciok: { feltetelesKitoltes?: boolean } = {}): number {
    this.futasKezd();
    this.szamitottMost = new Map();
    this.szamitottOkMost = new Map();
    let valtozott = 0;
    const sorrend = [...this.formok.filter((f) => !f.fo), ...this.formok.filter((f) => f.fo)];
    valtozott += this.webesHid(adat);
    for (let kor = 0; kor < 5; kor++) {
      let n = 0;
      const memo = this.ujMemo(false);
      for (const f of sorrend) {
        this.peldanyok(adat, f).forEach((p, pi) => {
          if (!p || typeof p !== 'object') return;
          const c0 = this.ktx(adat, f, pi, p, null, 0, memo, 'szamol');
          for (const info of f.szamitasSorrend) {
            const db = this.lapDbKtx(c0, info.lap);
            for (let k = 0; k < db; k++) {
              if (info.hely.csakElsoLap && k > 0) continue;
              const c = this.ktx(adat, f, pi, p, info, k, memo, 'szamol');
              const v = this.szamitErtek(c, info);
              if (v === undefined) continue;
              /*  A mező MOST tényleg kapott számított értéket. Ezt külön tartjuk nyilván, mert
               *  az ÁNYK sok mezőt csak FELTÉTELESEN számol (`feltételes_értékadás`): amíg a
               *  feltétel nem teljesül, a képlet nem ad értéket, és a mezőt a felhasználónak
               *  kell kitöltenie. Ilyenkor nem szabad csak olvashatóvá tenni. */
              let halmaz = this.szamitottMost.get(f.id);
              if (!halmaz) { halmaz = new Set<string>(); this.szamitottMost.set(f.id, halmaz); }
              halmaz.add(info.fid);
              /*  Miért nem írható a mező? A szabály saját üzenete a legjobb magyarázat
               *  („A mező csak akkor jelölhető, ha a képviselő az ügycsoport intézésére
               *  jogosult"), enélkül a felhasználó csak egy indokolatlanul szürke dobozt lát. */
              const uz = szoveg(this.utolsoSzamito?.uzenet).replace(/#13/g, ' ').replace(/\s+/g, ' ').trim();
              if (uz) {
                let okok = this.szamitottOkMost.get(f.id);
                if (!okok) { okok = new Map<string, string>(); this.szamitottOkMost.set(f.id, okok); }
                okok.set(info.fid, uz);
              }
              if (this.ir(p, info, k, v as Ertek)) n++;
            }
          }
          n += this.lookupToltes(c0, p, f);
          if (opciok.feltetelesKitoltes !== false) {
            for (const { info, sz } of f.feltetelesSzabalyok) {
              if (sz.ismeretlen.length) continue;   // nem értékelhető szabályból nem javaslunk értéket
              const db = this.lapDbKtx(c0, info.lap);
              for (let k = 0; k < db; k++) {
                const c = this.ktx(adat, f, pi, p, info, k, memo, 'szamol');
                c.feltetelesIr = true; c.jelzesek = [];
                this.kiertekel(c, sz);
                for (const j of c.jelzesek) {
                  const o = this.tarolo(p, j.cel.hely, j.k, false);
                  const most = o ? this.nyersbol(o[j.cel.hely.aux ? j.cel.fid : j.cel.hely.kod], j.cel) : null;
                  if (most !== null || j.cel.szamitas.length) continue;
                  if (this.ir(p, j.cel, j.k, konstans(j.szamitott, j.cel.tipus))) n++;
                }
              }
            }
          }
        });
      }
      valtozott += n;
      if (!n) break;
    }
    this.futasZar();
    return valtozott;
  }

  /*  WEBES ↔ ASZTALI MEZŐPÁR. A motor webes kitöltőként fut (anyk_param), és néhány sablon ilyenkor
   *  egy ONYA-mezőt vár bevitelnek, amelyet az ÁNYK-lapkép csak 3 képpontos dobozokkal, jelképesen
   *  rajzol (ATVUT17: `onya_számlaszám` 24 jegyen, a lapon a 8 + 16 jegyű asztali pár áll). A
   *  felhasználó a lapkép mezőit tölti, a webes mezőt ebből képezzük – így a webes ellenőrzés
   *  („A számlaszám webes felületen 24 karakteres”) és a bankkeresés is a megadott számlát látja. */
  private static readonly WEBES_PAROK: { cel: string; reszek: string[]; hossz: number }[] = [
    { cel: 'onya_számlaszám', reszek: ['Pénzintézet_azonosító', 'Alszámlaszám'], hossz: 24 },
  ];
  private webesHid(adat: any): number {
    let n = 0;
    for (const f of this.formok) {
      for (const par of AnykSzabalymotor.WEBES_PAROK) {
        const cel = f.mezok.get(f.vidFid.get(par.cel) || '');
        const reszek = par.reszek.map((v) => f.mezok.get(f.vidFid.get(v) || ''));
        if (!cel || reszek.some((r) => !r)) continue;
        this.peldanyok(adat, f).forEach((p) => {
          if (!p || typeof p !== 'object') return;
          const ertekek = reszek.map((r) => { const o = this.tarolo(p, r!.hely, 0, false); return o ? String(this.nyersbol(o[r!.hely.aux ? r!.fid : r!.hely.kod], r!) ?? '') : ''; });
          //  Nem pótoljuk ki 24 jegyre: a sablon a részeket a helyük szerint veti össze (9–24. jegy =
          //  alszámla), a 8 jegyű alszámla mögé írt nullák ott eltérést jeleznének.
          const v = ertekek.join('').replace(/\D/g, '').slice(0, par.hossz);
          if (this.ir(p, cel, 0, v || (null as any))) n++;
        });
      }
    }
    return n;
  }

  private ir(p: any, info: MezoInfo, k: number, v: Ertek): boolean {
    if (v === NINCS) return false;          // „a szabály nem mond semmit” – a mező marad, ahogy van
    const uj = this.irando(v, info);
    const kulcs = info.hely.aux ? info.fid : info.hely.kod;
    const o = this.tarolo(p, info.hely, k, uj !== undefined);
    if (!o) return false;
    const regi = o[kulcs];
    if (uj === undefined) {
      if (regi === undefined || regi === null || regi === '' || (regi === false && info.logikai)) { return false; }
      delete o[kulcs];
      return true;
    }
    if (regi === uj || (regi !== undefined && regi !== null && String(regi) === String(uj) && typeof regi === typeof uj)) return false;
    o[kulcs] = uj;
    return true;
  }

  /*  Melyik mező kapott a LEGUTÓBBI számolásnál tényleg értéket (formonként). A `szamitottMezok`
   *  ehhez képest az összes olyan mezőt adja, amelyhez EGYÁLTALÁN tartozik képlet. */
  private szamitottMost = new Map<string, Set<string>>();
  private szamitottOkMost = new Map<string, Map<string, string>>();

  /** A lezárt mezők indoklása (fid → a számító szabály üzenete) – a felület ezt mutatja súgóként. */
  szamitottOkok(form?: string): Map<string, string> {
    const r = new Map<string, string>();
    for (const [id, okok] of this.szamitottOkMost) {
      if (form && id !== formNevbol(form)) continue;
      for (const [fid, uz] of okok) r.set(fid, uz);
    }
    return r;
  }

  /**
   *  A felületen csak olvashatóként mutatandó mezők. Nem elég azt nézni, hogy van-e a mezőnek
   *  képlete: az ÁNYK-ban sok mező csak feltételesen számított (pl. az ÚJEGYKE főlapján az
   *  ügycsoport-jelölők csak bizonyos jogcímeknél állnak be maguktól), és amíg a képlet nem ad
   *  értéket, a mezőt KI KELL tudni tölteni. Ezért a legutóbbi számolás eredményét vesszük
   *  alapul; ha még nem futott számolás, üres (tehát semmi sem tilos feleslegesen).
   */
  szamitottAktualis(form?: string): Set<string> {
    const r = new Set<string>();
    for (const [id, halmaz] of this.szamitottMost) {
      if (form && id !== formNevbol(form)) continue;
      for (const fid of halmaz) r.add(fid);
    }
    return r;
  }

  /** A számítással RENDELKEZŐ mezők. `form` megadásával az adott form ENYK-fid-jei; nélküle az összes form uniója. */
  szamitottMezok(form?: string): Set<string> {
    const r = new Set<string>();
    for (const f of this.formok) {
      if (form && f.id !== formNevbol(form)) continue;
      for (const i of f.szamitasSorrend) r.add(i.fid);
    }
    return r;
  }

  /** Lapengedélyek (page_check): lapnév → { nyithato, ok (az ÁNYK üzenete, ha nem nyitható), pid }. */
  lapAllapot(adat: any, form: string, peldany: number): Record<string, { nyithato: boolean; ok?: string; pid?: string; szint?: string }> {
    this.futasKezd();
    const r: Record<string, { nyithato: boolean; ok?: string; pid?: string; szint?: string }> = {};
    try {
      const f = this._form(form);
      if (!f) return r;
      const p0 = this.peldanyok(adat, f)[peldany];
      const c = this.ktx(adat, f, peldany, p0 && typeof p0 === 'object' ? p0 : {}, null, 0, this.ujMemo(true), 'ellenoriz');
      for (const lap of f.lapok) {
        const e = this.lapEngedely(c, lap);
        r[lap.nev] = e.nyithato ? { nyithato: true, pid: lap.pid } : { nyithato: false, ok: e.sz?.uzenet, pid: lap.pid, szint: e.sz?.szint };
      }
    } catch { /* hibatűrő */ }
    this.futasZar();
    return r;
  }

  /** A dinamikus lapok példányszáma (lapnév → db) az adat szerint. */
  lapPeldanyok(adat: any, form: string, peldany: number): Record<string, number> {
    const r: Record<string, number> = {};
    const f = this._form(form);
    if (!f) return r;
    const p0 = this.peldanyok(adat, f)[peldany];
    const c = this.ktx(adat, f, peldany, p0 && typeof p0 === 'object' ? p0 : {}, null, 0, this.ujMemo(true), 'ellenoriz');
    for (const lap of f.lapok) if (lap.dinamikus) r[lap.nev] = this.lapDbKtx(c, lap);
    return r;
  }

  /** Hol él egy ENYK-mező az adatban (a lapleképező ugyanígy köti a mezőket). */
  fidHely(form: string, fid: string): AnykFidHely | null {
    const f = this._form(form);
    const i = f?.mezok.get(fid);
    return i ? JSON.parse(JSON.stringify(i.hely)) : null;
  }

  /** Egy ENYK-mező konkrét címe a lapIndex-edik lappéldányon: { ut (tároló útja), kulcs, aux }. */
  fidUt(form: string, fid: string, lapIndex = 0): { ut: string; kulcs: string; aux: boolean } | null {
    const f = this._form(form);
    const i = f?.mezok.get(fid);
    if (!i || (i.hely.csakElsoLap && lapIndex > 0)) return null;
    return { ut: this.utEpit(i.hely, lapIndex), kulcs: i.hely.aux ? i.fid : i.hely.kod, aux: i.hely.aux };
  }

  /** (ut, XSD-kód vagy ENYK-fid) → ENYK-mező + lappéldány. */
  private helyUtbol(f: FormInfo, ut: string, fid: string): { info: MezoInfo; k: number } | null {
    const lepesek = ut ? ut.split('/').map((s) => {
      const fej = /^(.*)#fej$/.exec(s);
      if (fej) return { nev: fej[1], fej: true, idx: 0 };
      const m = /^(.*)\[(\d+)\]$/.exec(s);
      return m ? { nev: m[1], fej: false, idx: +m[2] } : { nev: s, fej: false, idx: 0 };
    }) : [];
    const jeloltek = new Set<string>([...(f.kodFidek.get(fid) || []), ...(f.mezok.has(fid) ? [fid] : [])]);
    for (const jf of jeloltek) {
      const info = f.mezok.get(jf)!;
      const h = info.hely;
      if (h.lancok.length !== lepesek.length) continue;
      let k = 0, jo = true;
      h.lancok.forEach((l, i) => {
        const s = lepesek[i];
        if (!jo || s.nev !== l.nev || !!s.fej !== !!l.fej) { jo = false; return; }
        if (l.fej) return;
        if (l.laponkent) {
          const d = s.idx - (l.sor || 0);
          if (d < 0 || d % l.laponkent) { jo = false; return; }
          k = d / l.laponkent;
        } else if (s.idx !== (l.sor || 0)) jo = false;
      });
      if (jo && (!h.csakElsoLap || k === 0)) return { info, k };
    }
    return null;
  }

  /** Az utolsó hívás lefedettsége és a fordítási statisztika. */
  statisztika(): AnykStatisztika {
    return JSON.parse(JSON.stringify(this.stat));
  }

  /** Az utolsó hívásban kiértékelt szabályok kulcsai (lefedettség-méréshez). */
  utolsoKiertekeltek(): string[] { return Array.from(this.kiertekeltek); }

  /** A fordításkor ismeretlen függvényt tartalmazó szabályok száma. */
  ismeretlenSzabalyok(): number {
    let n = 0;
    const szamol = (l: Szabaly[]) => l.forEach((s) => { if (s.ismeretlen.length) n++; });
    for (const f of this.formok) {
      f.mezok.forEach((i) => { szamol(i.ellenorzes); szamol(i.szamitas); });
      szamol(f.formEllenorzes); f.lapok.forEach((l) => szamol(l.ellenorzes));
    }
    return n;
  }

  private futasKezd() { this.kiertekeltek = new Set(); this.futasiHibak = new Map(); this.ismeretlenErintett = new Set(); }
  private futasZar() {
    this.stat.kiertekelt = this.kiertekeltek.size;
    this.stat.futasiHiba = this.futasiHibak.size;
    this.stat.ismeretlentErinto = this.ismeretlenErintett.size;
    this.stat.futasiHibak = Array.from(this.futasiHibak.entries()).slice(0, 50).map(([szabaly, uzenet]) => ({ szabaly, uzenet }));
  }
}

const FOLYAMATBAN = { folyamatban: true };

/* ============================== FÜGGVÉNYTÁR ============================== */

interface Ford { m: AnykSzabalymotor; f: FormInfo; sz: Szabaly; ford: (x: any) => Fv; fordFormban: (formId: string, x: any) => Fv; }
type Gyarto = (args: any[], fk: Ford) => Fv;

/** Mohó kiértékelés (az ÁNYK minden paramétert előre kiszámol, kivéve if / imp / ha_n és a lapközi függvényeket). */
function moho(impl: (v: Ertek[], c: Ktx) => Ertek): Gyarto {
  return (args, fk) => {
    const a = args.map((x) => fk.ford(x));
    if (a.length === 1) { const f0 = a[0]; return (c) => impl([f0(c)], c); }
    if (a.length === 2) { const f0 = a[0], f1 = a[1]; return (c) => impl([f0(c), f1(c)], c); }
    return (c) => impl(a.map((f) => f(c)), c);
  };
}

/** Konstans szöveg-paraméter (mezőazonosító, vid, formnév). */
function konstSzoveg(n: any): string | null { return n && typeof n === 'object' && !Array.isArray(n) && typeof n.s === 'string' ? n.s : null; }

/** kitöltött-féle függvények paramétere: érték + (ha mezőhivatkozás) a „0 is kitöltött” jelző (calc_ze). */
function kitoltottArg(args: any[], fk: Ford): { fn: Fv; calcZe: boolean | null }[] {
  return args.map((x) => {
    const fn = fk.ford(x);
    let calcZe: boolean | null = null;
    if (Array.isArray(x) && String(x[0]).toLowerCase() === 'field') {
      const fid = konstSzoveg(x[1]);
      const i = fid ? fk.m._mezo(fk.f, fid) : undefined;
      if (i) calcZe = i.calcZe;
    }
    return { fn, calcZe };
  });
}
/** kitoltottNumField: a "0" csak calc_ze-s mezőben számít kitöltöttnek (nem mezőhivatkozásnál igen). */
function kitoltott(v: Ertek, calcZe: boolean | null): boolean {
  if (v === null || v === undefined) return false;
  if (typeof v === 'string') return v.length > 0;
  if (typeof v === 'boolean') return v;
  if (typeof v === 'number') return v !== 0 || calcZe === null || calcZe;
  return true;
}

/** Egy mezőre hivatkozó closure (a field-függvény). */
function mezoFv(fk: Ford, arg: any, szerint: 'fid' | 'vid'): (c: Ktx) => MezoInfo | null {
  const s = konstSzoveg(arg);
  if (s !== null) {
    const fid = szerint === 'vid' ? fk.f.vidFid.get(s) : s;
    const info = fid ? fk.m._mezo(fk.f, fid) : undefined;
    if (!info) { fk.m._ismeretlenMezo(s); return () => null; }
    return () => info;
  }
  const a = fk.ford(arg);
  return (c) => {
    const v = a(c);
    if (typeof v !== 'string') return null;
    const fid = szerint === 'vid' ? c.form.vidFid.get(v) : v;
    return (fid && c.form.mezok.get(fid)) || null;
  };
}

/** A kifejezésben hivatkozott dinamikus lapok (field / akt_din_érték). */
function dinLapok(n: any, f: FormInfo, ki: Set<string>) {
  if (!Array.isArray(n)) return;
  const fn = String(n[0]).toLowerCase();
  if (fn === 'field' || fn === 'akt_din_érték' || fn === 'adózói_érték') {
    const fid = konstSzoveg(n[1]);
    const i = fid ? f.mezok.get(fid) : undefined;
    if (i && i.lap.dinamikus) ki.add(i.lap.pid);
  }
  n.slice(1).forEach((x: any) => dinLapok(x, f, ki));
}

function relacio(op: string): Gyarto {
  return moho((v) => {
    let ered: Ertek = null;
    let bal = szamra(v[0]);
    if (typeof bal !== 'number') return null;
    for (let i = 1; i < v.length; i++) {
      const jobb = szamra(v[i]);
      if (typeof jobb !== 'number') break;
      ered = muvelet(op, bal, jobb);
      if (ered === false) break;
      bal = jobb;
    }
    return ered;
  });
}

function egyenloseg(op: '=' | '!='): Gyarto {
  return moho((v) => {
    const t0 = v.map(tp).find((t) => t !== 0) || 0;
    const konv = (x: Ertek) => (x === null ? (t0 === 1 ? '' : t0 === 2 ? 0 : null) : x);
    let ered: Ertek = null;
    let bal = konv(v[0]);
    const tb = tp(bal);
    if (op === '!=' && tb > 4) return null;
    for (let i = 1; i < v.length; i++) {
      const jobb = konv(v[i]);
      const tj = tp(jobb);
      if (op === '!=' && tj !== 0 && tb !== 0 && tj !== tb) return null; // ÁNYK: típuseltérés
      ered = muvelet(op, bal, jobb);
      if (ered === false) break;
      bal = jobb;
    }
    return ered;
  });
}

function logikai(op: '&' | '|'): Gyarto {
  return moho((v) => {
    let ered: Ertek = null;
    let bal = v[0];
    const tb = tp(bal);
    if (tb > 4) return null;
    for (let i = 1; i < v.length; i++) {
      const jobb = v[i];
      const tj = tp(jobb);
      if (tj > 4 || (tj !== tb && tj !== 0 && tb !== 0)) return null;
      ered = muvelet(op, bal, jobb);
      if (ered === false) break;
      bal = jobb;
    }
    return ered;
  });
}

function matrixKeres(m: AnykSzabalymotor, nev: string, hatarolo: string, szurok: { oszlop: number; rel: string; ertek: string }[], elso: boolean): string[][] | null {
  //  A külső (ÁNYK-erőforrás, „…#…”) mátrixot a kiszolgáló adja hozzá, ha pótolni tudja (pl. az
  //  irányítószám-törzs); ha nincs meg, ugyanúgy nil, mint eddig.
  const sorok = m._matrix(nev);
  if (!sorok) return null;
  const r: string[][] = [];
  for (const s of sorok) {
    const oszl = s.split(hatarolo);
    let jo = true;
    for (const sz of szurok) {
      const x = oszl[sz.oszlop] ?? '';
      if (sz.rel === 'tartalmaz' ? x.indexOf(sz.ertek) < 0 : x !== sz.ertek) { jo = false; break; }
    }
    if (jo) { r.push(oszl); if (elso) break; }
  }
  return r;
}

/** Átfedés-ellenőrzés (nincsátfedés / nincsátfedés2): a „[lap]_B[520..522]” alakú mezőcsoport feloldása. */
function csoportFidek(f: FormInfo, spec: string): string[] {
  if (f.mezok.has(spec)) return [spec];
  const m = /^\[[^\]]*\]_([^\[]+)\[(\d+)\.\.(\d+)\]$/.exec(spec);
  if (!m) return f.vidFid.has(spec) ? [f.vidFid.get(spec)!] : [];
  const r: string[] = [];
  for (let i = +m[2]; i <= +m[3]; i++) { const fid = f.vidFid.get(m[1] + i); if (fid) r.push(fid); }
  return r;
}

const NEM_TAMOGATOTT = new Set<string>(['napok_száma']);

/*  „NINCS SZÁMÍTOTT ÉRTÉK” jelzés. Különbözik a nil-től: a nil azt jelenti, hogy a számítás
 *  eredménye ÜRES (a mezőt ki kell üríteni), ez viszont azt, hogy a szabály MOST nem mond semmit
 *  a mezőről – tehát a mező a felhasználóé marad, sem felülírni, sem lezárni nem szabad.
 *  (`getRealZero` az objektumokat változatlanul átengedi, így a burkoláson is átjut.) */
const NINCS: any = { nincsErtek: true };

const FUGGVENYEK: Record<string, Gyarto> = {
  /* ── vezérlés (lusta) ── */
  if: (args, fk) => {
    const [a, b, e] = args.map((x) => fk.ford(x));
    return (c) => {
      const f = a(c);
      if (f === null) return null;
      if (typeof f !== 'boolean') return null;
      return f ? (b ? b(c) : null) : (e ? e(c) : null);
    };
  },
  imp: (args, fk) => {
    const [a, b] = args.map((x) => fk.ford(x));
    return (c) => {
      const f = a(c);
      if (f === null) return null;
      if (typeof f !== 'boolean') throw new AnykFutasiHiba('imp: az első paraméter nem logikai');
      return f ? (b ? b(c) : null) : true;
    };
  },
  ha_n: (args, fk) => {
    const a = args.map((x) => fk.ford(x));
    return (c) => {
      for (let i = 0; i + 1 < a.length; i += 2) {
        const f = a[i](c);
        if (f === null) return null;
        if (typeof f !== 'boolean') throw new AnykFutasiHiba('ha_n: nem logikai feltétel');
        if (f) return a[i + 1](c);
      }
      return a.length ? a[a.length - 1](c) : null;
    };
  },
  getrealzero: (args, fk) => {
    const a = fk.ford(args[0]);
    return (c) => {
      const v = a(c);
      if (typeof v === 'boolean' || (v !== null && typeof v === 'object')) return v;
      const nulla = v === 0 || v === '0' || v === '0.0';
      if (!(v === null || v === '' || nulla)) return v;
      const t = c.self ? c.self.tipus : tp(v);
      if (t !== 2) return v;
      if (c.zerus) return 0;
      if (nulla && c.mindUres) return null;
      return v;
    };
  },

  /* ── mezők ── */
  field: (args, fk) => {
    const mi = mezoFv(fk, args[0], 'fid');
    return (c) => { const i = mi(c); return i ? c.m.mezoErtekKtx(c, i) : null; };
  },
  'adózói_érték': (args, fk) => FUGGVENYEK.field(args, fk),
  'akt_din_érték': (args, fk) => {
    const mi = mezoFv(fk, args[0], 'fid');
    return (c) => { const i = mi(c); return i ? konstans(c.m.nyersKtx(c, i, i.lap.dinamikus ? c.k : 0), i.tipus) : null; };
  },
  dsum: (args, fk) => {
    const mi = mezoFv(fk, args[0], 'vid');
    return (c) => {
      const i = mi(c);
      if (!i) return null;
      const n = c.m.lapDbKtx(c, i.lap);
      let s: number | null = null;
      for (let k = 0; k < n; k++) {
        const v = konstans(c.m.nyersKtx(c, i, k), i.tipus);
        if (!c.valtozoban && !uresErtek(v)) c.mindUres = false;
        if (v === null) continue;
        if (typeof v !== 'number') throw new AnykFutasiHiba('DSum: nem szám');
        s = (s ?? 0) + v;
      }
      return s;
    };
  },
  dprev: (args, fk) => {
    const mi = mezoFv(fk, args[0], 'vid');
    return (c) => {
      const i = mi(c);
      if (!i || !i.lap.dinamikus) return null;
      const k = c.m.indexKtx(c, i);
      if (k <= 0) return 0;
      const v = konstans(c.m.nyersKtx(c, i, k - 1), i.tipus);
      return v === null ? 0 : v;
    };
  },
  dpagenumber: () => (c) => (c.self ? c.k + 1 : null),
  sor: (args, fk) => { const mi = mezoFv(fk, args[0], 'vid'); return (c) => { const i = mi(c); return i && i.sor != null ? i.sor : null; }; },
  oszlop: (args, fk) => { const mi = mezoFv(fk, args[0], 'vid'); return (c) => { const i = mi(c); return i && i.oszlop != null ? i.oszlop : null; }; },

  /* ── dinamikus lapok kapcsolata ── */
  din_lap_kapcsol: (args, fk) => {
    const lapok = new Set<string>();
    dinLapok(args[0], fk.f, lapok);
    const e = fk.ford(args[0]);
    return (c) => {
      const sajat = c.self && c.self.lap.dinamikus ? c.self.lap.pid : null;
      const l = Array.from(lapok).filter((p) => p !== sajat);
      if (!l.length) return 0;
      const db = l.map((p) => c.m.lapDbKtx(c, c.form.lapPid.get(p)!));
      if (db.some((n) => n <= 0)) return 0;
      const regi = c.felulir;
      const idx = db.map(() => 0);
      let n = 0;
      try {
        for (;;) {
          const fm = new Map(regi || []);
          l.forEach((p, i) => fm.set(p, idx[i]));
          c.felulir = fm;
          const v = e(c);
          if (typeof v !== 'boolean') return null; // ÁNYK: „Számítási hiba” → nil
          if (v) n++;
          let j = 0;
          while (j < idx.length && ++idx[j] >= db[j]) { idx[j] = 0; j++; }
          if (j >= idx.length) break;
        }
      } finally { c.felulir = regi; }
      return n;
    };
  },
  din_oldal_kapcsol: (args, fk) => {
    const lapok = new Set<string>();
    dinLapok(args[0], fk.f, lapok);
    const e = fk.ford(args[0]);
    return (c) => {
      if (lapok.size > 1) return null;
      const sajat = c.self ? c.self.lap.pid : null;
      if (lapok.size === 1 && (!sajat || !lapok.has(sajat))) return null;
      if (!lapok.size) return 0;
      const lap = c.form.lapPid.get(sajat!)!;
      const db = c.m.lapDbKtx(c, lap);
      const regi = c.felulir;
      let n = 0;
      try {
        for (let j = 0; j < db; j++) {
          if (j === c.k) continue;
          const fm = new Map(regi || []);
          fm.set(lap.pid, j);
          c.felulir = fm;
          const v = e(c);
          if (typeof v !== 'boolean') return null;
          if (v) n++;
        }
      } finally { c.felulir = regi; }
      if (n === 0 && db > 1) c.mindUres = false;
      return n;
    };
  },

  /* ── formok közötti ── */
  globsum: (args, fk) => {
    const formNev = konstSzoveg(args[0]);
    // a belső kifejezés a célform mezőire (vid-jeire) hivatkozik
    const e = formNev ? fk.fordFormban(formNev, args[1]) : fk.ford(args[1]);
    const kulcs = fk.sz.kulcs + ':' + JSON.stringify(args[1]);
    return (c) => {
      const f = formNev ? c.m._form(formNev) : undefined;
      if (!f) return null;
      const gyk = f.id + '|' + kulcs;
      if (c.memo.gyorsit && c.memo.glob.has(gyk)) return c.memo.glob.get(gyk)!;
      let s: number | null = null;
      c.m._peldanyok(c.adat, f).forEach((q, qi) => {
        const c2 = c.m._ktx(c, f, qi, q && typeof q === 'object' ? q : {});
        let v = e(c2);
        if (v === null) return;
        if (typeof v === 'string') { if (!SZAM_RE.test(v)) return; v = Number(v); }
        if (typeof v !== 'number') return;
        s = (s ?? 0) + v;
      });
      if (c.memo.gyorsit) c.memo.glob.set(gyk, s);
      return s;
    };
  },
  compare_am: (args, fk) => {
    const [fa, fida, , fidm, op] = args.map(konstSzoveg);
    const miM = fidm ? mezoFv(fk, args[3], 'fid') : () => null;
    const opK = op === '<>' ? '!=' : op;
    return (c) => {
      if (!fa || !fida || !opK || ['=', '<', '>', '<=', '>=', '!='].indexOf(opK) < 0) return null;
      const f = c.m._form(fa);
      const q = f ? c.m._peldanyok(c.adat, f)[0] : undefined;
      if (!f || !q) return false;
      const ia = f.mezok.get(fida);
      if (!ia) return false;
      const c2 = c.m._ktx(c, f, 0, q);
      let va = konstans(c.m.nyersKtx(c2, ia, 0), ia.tipus);
      const im = miM(c);
      let vm = im ? c.m.mezoErtekKtx(c, im) : null;
      const ta = tp(va), tm = tp(vm);
      if (tm > 4 || (tm !== ta && tm !== 0 && ta !== 0)) return null;
      if (tm === 4 && ta === 0) va = false;
      if (tm === 0 && ta === 4) vm = false;
      const r = muvelet(opK, va, vm);
      return typeof r === 'boolean' ? r : null;
    };
  },
  unique_m: (args, fk) => {
    const formNev = konstSzoveg(args[0]);
    const fid = konstSzoveg(args[1]);
    return (c) => {
      const f = formNev ? c.m._form(formNev) : undefined;
      const i = f && fid ? f.mezok.get(fid) : undefined;
      if (!f || !i) return true;
      const gyk = 'u|' + f.id + '|' + fid;
      if (c.memo.gyorsit && c.memo.glob.has(gyk)) return c.memo.glob.get(gyk)!;
      const latott = new Set<string>();
      let ok = true;
      c.m._peldanyok(c.adat, f).forEach((q, qi) => {
        if (!ok) return;
        const c2 = c.m._ktx(c, f, qi, q && typeof q === 'object' ? q : {});
        const v = c.m.mezoErtekKtx(c2, i);
        if (v === null) return;
        const s = szoveg(v);
        if (latott.has(s)) ok = false; else latott.add(s);
      });
      if (c.memo.gyorsit) c.memo.glob.set(gyk, ok);
      return ok;
    };
  },

  /* ── logika, reláció ── */
  '&': logikai('&'),
  '|': logikai('|'),
  not: moho((v) => (typeof v[0] === 'boolean' ? !v[0] : null)),
  ekv: moho((v) => {
    if (v[0] === null || v[1] === null) return null;
    if (typeof v[0] !== 'boolean' || typeof v[1] !== 'boolean') throw new AnykFutasiHiba('ekv: nem logikai');
    return v[0] === v[1];
  }),
  '=': egyenloseg('='),
  '!=': egyenloseg('!='),
  '<': relacio('<'), '>': relacio('>'), '<=': relacio('<='), '>=': relacio('>='),
  in: moho((v) => {
    if (v.length < 2) return null;
    const a = v[0] === null ? null : (szoveg(v[0]) || null);
    for (let i = 1; i < v.length; i++) {
      const b = v[i] === null ? null : (szoveg(v[i]) || null);
      if (a === null && b === null) return true;
      if (a !== null && b !== null && a === b) return true;
    }
    return false;
  }),

  /* ── aritmetika ── */
  '+': moho((v) => {
    let i = 0;
    while (i < v.length && v[i] === null) i++;
    if (i >= v.length) return null;
    let ered: Ertek = v[i];
    const t = tp(ered);
    if (t !== 1 && t !== 2) return null;
    for (i++; i < v.length; i++) {
      let x = v[i];
      if (x === null) continue;
      const tx = tp(x);
      if (tx !== 1 && tx !== 2) return null;
      if (t === 2 && tx === 1) { if (!SZAM_RE.test(x as string)) return null; x = Number(x); }
      ered = muvelet('+', ered, x);
    }
    return ered;
  }),
  '-': moho((v) => {
    let ered = szamra(v[0]);
    if (typeof ered !== 'number') return null;
    for (let i = 1; i < v.length; i++) {
      const x = szamra(v[i]);
      if (typeof x !== 'number') return null;
      ered = (ered as number) - x;
    }
    return ered;
  }),
  '*': moho((v) => {
    if (v.some((x) => x === null)) return 0;
    let ered = szamra(v[0]);
    if (typeof ered !== 'number') return null;
    for (let i = 1; i < v.length; i++) { const x = szamra(v[i]); if (typeof x !== 'number') return null; ered = (ered as number) * x; }
    return ered;
  }),
  '/': moho((v) => {
    if (v.some((x) => x === null)) return 0;
    let ered = szamra(v[0]);
    if (typeof ered !== 'number') return null;
    for (let i = 1; i < v.length; i++) { const x = szamra(v[i]); if (typeof x !== 'number') return null; if (x === 0) return null; ered = (ered as number) / x; }
    return ered;
  }),
  round: moho((v) => (typeof v[0] === 'number' ? kerekit(v[0]) : v[0] === undefined ? null : v[0])),
  abs: moho((v) => (typeof v[0] === 'number' ? Math.abs(v[0]) : null)),
  modulo: moho((v) => (typeof v[0] === 'number' && typeof v[1] === 'number' && v[1] !== 0 ? Number((v[0] % v[1]).toFixed(9)) : null)),
  max: moho((v) => {
    let ered: number | null = null;
    for (const x of v) {
      if (x === null) continue;
      if (typeof x !== 'number') return ered === null ? null : ered;
      ered = ered === null ? x : Math.max(ered, x);
    }
    return ered;
  }),
  min: moho((v) => {
    // ÁNYK: az első nil paraméter 0-nak számít; ha az első utáni összes nil, az eredmény nil.
    if (!v.length) return null;
    let ered = v[0] === null ? 0 : v[0];
    if (typeof ered !== 'number') return null;
    if (v.length === 1) return ered;
    let nil = 0;
    for (let i = 1; i < v.length; i++) {
      const x = v[i];
      if (x === null) { nil++; continue; }
      if (typeof x !== 'number') break;
      ered = Math.min(ered as number, x);
    }
    return nil === v.length - 1 ? null : ered;
  }),
  sum: moho((v) => {
    let s = 0;
    for (const x of v) { if (x === null) continue; if (typeof x !== 'number') break; s += x; }
    return s;
  }),
  sumpos: moho((v) => { let s = 0; for (const x of v) { if (x === null) continue; if (typeof x !== 'number') break; if (x > 0) s += x; } return s; }),
  sumneg: moho((v) => { let s = 0; for (const x of v) { if (x === null) continue; if (typeof x !== 'number') break; if (x < 0) s += x; } return s; }),

  /* ── kitöltöttség ── */
  'kitöltött': (args, fk) => {
    const a = kitoltottArg(args, fk);
    return (c) => { let ok = true; for (const x of a) { const v = x.fn(c); if (ok && !kitoltott(v, x.calcZe)) ok = false; } return ok; };
  },
  'kitöltöttdarab': (args, fk) => {
    const a = kitoltottArg(args, fk);
    return (c) => { let n = 0; for (const x of a) if (kitoltott(x.fn(c), x.calcZe)) n++; return n; };
  },
  unique: (args, fk) => {
    const a = kitoltottArg(args, fk);
    return (c) => {
      const v = a.map((x) => { const e = x.fn(c); return kitoltott(e, x.calcZe) ? szoveg(e) : null; });
      const l = new Set<string>();
      for (const s of v) { if (s === null) continue; if (l.has(s)) return false; l.add(s); }
      return true;
    };
  },
  'együttesenkitöltendő': (args, fk) => {
    const a = kitoltottArg(args, fk);
    return (c) => { const k = a.map((x) => kitoltott(x.fn(c), x.calcZe)); return k.every((x) => x === k[0]); };
  },

  /* ── szöveg ── */
  len: moho((v) => (v[0] === null ? null : szoveg(v[0]).length)),
  trim: moho((v) => {
    if (typeof v[0] !== 'string') return null;
    if (v.length > 1 && typeof v[1] === 'string' && v[1].length === 1) {
      const s = v[0], ch = v[1];
      let a = 0, b = s.length;
      while (a < b && s[a] === ch) a++;
      while (b > a && s[b - 1] === ch) b--;
      return s.slice(a, b);
    }
    return v[0].trim();
  }),
  substr: moho((v) => {
    const [s, k, h] = v;
    if (typeof s !== 'string' || typeof k !== 'number' || typeof h !== 'number') return null;
    const b = Math.trunc(k) - 1;
    let l = Math.trunc(h);
    const L = s.length;
    if (L === 0) return '';
    if (L < b + l) l = L - b;
    if (b > -1 && L >= b && L >= b + l) {
      if (l < 0) return s.substring(b + l > 0 ? l + b : 0, b + 1);
      return s.substring(b, b + l);
    }
    return null;
  }),
  pos: moho((v) => {
    if (v[0] === null || v[1] === null) return null;
    if (typeof v[0] !== 'string' || typeof v[1] !== 'string') throw new AnykFutasiHiba('pos: nem szöveg');
    if (!v[0].length) return null;
    return v[1].indexOf(v[0]) + 1;
  }),
  'kisbetüs': moho((v) => (typeof v[0] === 'string' ? v[0].toLowerCase() : null)),
  'nagybetüs': moho((v) => (typeof v[0] === 'string' ? v[0].toUpperCase() : '')),
  string: moho((v) => (v[0] === null ? null : szoveg(v[0]))),
  'szám': moho((v) => {
    const szam = (x: Ertek): number | null => {
      if (x === null) return null;
      if (typeof x === 'number') return x;
      if (typeof x === 'boolean') return x ? 1 : 0;
      if (typeof x === 'string') return x.length && SZAM_RE.test(x) ? Number(x) : null;
      return null;
    };
    const a = szam(v[0]);
    return a !== null ? a : szam(v[1]);
  }),

  /* ── dátum (ééééhhnn szám) ── */
  'jódátum': moho((v) => (v[0] === null ? false : szoveg(v[0]) === '' ? true : joDatum(szoveg(v[0])))),
  'dátumév': moho((v) => {
    if (v[0] === null) return 0;
    if (typeof v[0] !== 'number') return null;
    const s = szoveg(v[0]);
    const n = parseInt(s.length <= 3 ? s : s.slice(0, 4), 10);
    return n > -1 && n < 10000 ? n : null;
  }),
  'dátumhó': moho((v) => {
    if (typeof v[0] !== 'number') return null;
    const s = szoveg(v[0]);
    const n = s.length <= 4 ? 0 : parseInt(s.length === 5 ? s.substr(4, 1) : s.substr(4, 2), 10);
    return n > 0 && n < 13 ? n : null;
  }),
  'dátumnap': moho((v) => {
    if (typeof v[0] !== 'number') return null;
    const s = szoveg(v[0]);
    const n = s.length <= 6 ? 0 : parseInt(s.length === 7 ? s.substr(6, 1) : s.substr(6, 2), 10);
    return n > 0 && n < 32 ? n : null;
  }),
  'dátumok_különbsége': moho((v) => {
    if (typeof v[0] !== 'number' || typeof v[1] !== 'number') return null;
    const a = szoveg(v[0]), b = szoveg(v[1]);
    return joDatum(a) && joDatum(b) ? datumNapok(b) - datumNapok(a) : 0;
  }),
  'dátumplusznap': moho((v) => {
    if (typeof v[0] !== 'number' || typeof v[1] !== 'number') return null;
    const a = szoveg(v[0]);
    return joDatum(a) ? napDatum(datumNapok(a) + Math.trunc(v[1])) : 0;
  }),
  'hónaputolsónapja': moho((v) => {
    if (typeof v[0] !== 'number' || typeof v[1] !== 'number') return null;
    for (let n = 31; n > 24; n--) { const s = String(v[0] * 10000 + v[1] * 100 + n); if (joDatum(s)) return +s; }
    return 0;
  }),

  /* ── azonosítók ── */
  'jóadószám': moho((v) => {
    const t = tp(v[0]);
    if (t === 0) return null;
    if (t !== 1 && t !== 2) throw new AnykFutasiHiba('jóadószám: típus');
    const s = szoveg(v[0]).trim().split('-').join('');
    if (chkInputIds(t, s, 11) < 0) return false;
    if (s.length === 11 && '12349'.indexOf(s[8]) >= 0 && '08'.indexOf(s[0]) < 0) {
      const m = parseInt(s.slice(9, 11), 10);
      if ((1 < m && m < 45 && m !== 21) || m === 51) return cdv8(s.slice(0, 8));
    }
    return false;
  }),
  'jóadóazonosító': moho((v) => {
    const t = tp(v[0]);
    if (t === 0) return null;
    if (t !== 1 && t !== 2) throw new AnykFutasiHiba('jóadóazonosító: típus');
    const s = szoveg(v[0]);
    if (t === 1 ? s.length === 0 : s === '0') return true;
    if (s.length !== 10 || !/^\d+$/.test(s) || s[0] !== '8') return false;
    let o = 0;
    for (let i = 0; i < 9; i++) o += (i + 1) * (s.charCodeAt(i) - 48);
    return s.charCodeAt(9) - 48 === o % 11;
  }),
  'jótajszám': moho((v) => {
    const t = tp(v[0]);
    if (t === 0) return null;
    if (t !== 1 && t !== 2) throw new AnykFutasiHiba('jótajszám: típus');
    const s = szoveg(v[0]);
    if (s === '000000000') return false;
    const e = chkInputIds(t, s, 9);
    if (e < 1) return e === 0;
    let p = 0, pa = 0;
    for (let i = 0; i < 8; i += 2) pa += s.charCodeAt(i) - 48;
    for (let i = 1; i < 8; i += 2) p += s.charCodeAt(i) - 48;
    return (p * 7 + pa * 3) % 10 === s.charCodeAt(8) - 48;
  }),
  'jóbárkód': moho((v) => {
    const t = tp(v[0]);
    if (t === 0) return null;
    if (t !== 1 && t !== 2) throw new AnykFutasiHiba('jóbárkód: típus');
    const s = szoveg(v[0]);
    const e = chkInputIds(t, s, 10);
    if (e < 1) return e === 0;
    const w = [9, 7, 3, 1, 9, 7, 3, 1, 9];
    let o = 0;
    for (let i = 0; i < 9; i++) o += (s.charCodeAt(i) - 48) * w[i];
    const k = (10 - (o % 10)) % 10, u = s.charCodeAt(9) - 48;
    return k === u || (k + 1) % 10 === u || (k + 2) % 10 === u;
  }),

  /* ── mátrixok ── */
  'mátrix_szűrés': moho((v) => {
    if (typeof v[0] !== 'string' || typeof v[1] !== 'string') return null;
    const szurok: { oszlop: number; rel: string; ertek: string }[] = [];
    for (let i = 2; i + 2 < v.length; i += 4) {
      if (v[i] === null) break;
      szurok.push({ oszlop: Number(v[i]) - 1, rel: szoveg(v[i + 1]) === 'tartalmaz' ? 'tartalmaz' : '=', ertek: szoveg(v[i + 2]) });
    }
    return { lista: true, matrix: v[0], hatarolo: v[1], szurok };
  }),
  /**
   *  ÉRTELMEZÉS: feltételes_értéklista(szabad, oszlop, megjelenített, lista[, csoport]) az ÁNYK-ban a
   *  lenyíló lista modellje (ellenőrzésként nem jelez). Itt: ha a lista NEM szabad beírású (1. param
   *  hamis) és a mező kitöltött, az értéknek szerepelnie kell a szűrt mátrix `oszlop`-adik oszlopában
   *  (pl. M350 „nem létező FEOR szám”). Ismeretlen (külső) mátrixnál nil.
   */
  'feltételes_értéklista': (args, fk) => FUGGVENYEK['feltételes_értéklista2'](args, fk),
  'feltételes_értéklista2': moho((v, c) => {
    const [szabad, oszlop, , lista] = v;
    if (typeof szabad !== 'boolean' || typeof oszlop !== 'number' || !lista || typeof lista !== 'object') return null;
    if (szabad || !c.self) return true;
    const ertek = szoveg(c.m.mezoErtekKtx(c, c.self));
    if (!ertek) return true;
    const l = lista as AnykLista;
    const sorok = matrixKeres(c.m, l.matrix, l.hatarolo, l.szurok, false);
    if (!sorok) return null;
    return sorok.some((s) => (s[oszlop - 1] ?? '') === ertek);
  }),
  /**
   *  ÉRTELMEZÉS: feltételes_érték(2)(feltétel, érték, vid[, üzenet]) – ha a feltétel igaz, a vid-mező
   *  értéke `érték` legyen. Az ÁNYK mezőszerkesztéskor beírja, teljes ellenőrzéskor figyelmeztet
   *  („Eltér a mező értéke a számított értéktől”); a függvény maga mindig IGAZ. Itt: ellenőrzéskor
   *  figyelmeztetés (a szabály üzenetével), szamol()-ban az üres célmező kitöltése.
   */
  /**
   *  feltételes_értékadás(feltétel, érték[, egyébként]) – az ÁNYK CSAK akkor írja be az értéket,
   *  ha a feltétel teljesül; különben a mező a felhasználóé marad.
   *
   *  Eddig ez a függvény hiányzott, és az ismeretlen függvény nil-t ad: a motor ezt „ürítsd ki”
   *  parancsnak vette, a mezőt pedig „számított”-ként lezárta. Így lett az ÚJEGYKE főlapjának
   *  „D” blokkja – ahol épp az ügycsoportokat kell megjelölni – kitölthetetlen, szürke mezőkkel.
   *  A nem teljesülő ágon ezért NINCS-et adunk vissza: a motor hozzá sem nyúl a mezőhöz.
   */
  'feltételes_értékadás': (args, fk) => {
    const felt = fk.ford(args[0]);
    const ertek = args.length > 1 ? fk.ford(args[1]) : null;
    const egyebkent = args.length > 2 ? fk.ford(args[2]) : null;
    const ervenyes = (v: Ertek) => (v === null || v === '' ? NINCS : v);
    return (c) => {
      const f = felt(c);
      if (f === true) return ervenyes(ertek ? ertek(c) : null);
      if (f === false && egyebkent) return ervenyes(egyebkent(c));
      return NINCS;
    };
  },
  'feltételes_érték': (args, fk) => FUGGVENYEK['feltételes_érték2'](args, fk),
  'feltételes_érték2': (args, fk) => {
    const cond = fk.ford(args[0]);
    const ertek = fk.ford(args[1]);
    const vid = konstSzoveg(args[2]);
    const cel = vid && !vid.startsWith('&') ? mezoFv(fk, args[2], 'vid') : () => null;
    return (c) => {
      const f = cond(c);
      const e = ertek(c);
      if (f !== true) return true;
      const i = cel(c);
      if (!i) return true;
      const k = c.m.indexKtx(c, i);
      const most = konstans(c.m.nyersKtx(c, i, k), i.tipus);
      let uj: Ertek = e;
      // ÉRTELMEZÉS: nil érték = a mező üres legyen (az ÁNYK számmezőbe 0-t írna).
      if (e === null || e === '') uj = i.tipus === 4 ? false : null;
      else if (i.tipus === 2) uj = szamra(e);
      else if (i.tipus === 4) uj = e === null || e === '' ? false : konstans(szoveg(e), 4);
      const a = uj === null ? '' : szoveg(uj);
      const b = most === null ? '' : szoveg(most);
      if (a !== b && !(i.tipus === 4 && a === 'false' && b === '')) {
        if (c.jelzesek) c.jelzesek.push({ cel: i, k, jelenlegi: b, szamitott: a });
      }
      return true;
    };
  },

  /* ── intervallumok ── */
  'nincsátfedés': (args, fk) => atfedes(args, fk, false),
  'nincsátfedés2': (args, fk) => atfedes(args, fk, true),
  /** Nem támogatott (napok száma sorok közötti összevonással): nil → nem jelez hibát. */
  'napok_száma': () => () => null,

  /* ── egyéb ── */
  verziokontroll: () => () => true,
  callgui: () => () => null,
};

/*  DPageCount("<lapnév>@…") – a megadott DINAMIKUS lap kitöltött példányainak száma.
 *  Ebből áll össze a főlap „benyújtott pótlapok száma” rovata (T201T p1…p19). Eddig hiányzott,
 *  ezért ezek a mezők üresen maradtak; a hivatkozás a lap nevével kezdődik, a '@' utáni rész az
 *  ÁNYK belső horgonya, azzal nincs dolgunk. */

/* ── ÁNYK-környezet (a kitöltés módja) ─────────────────────────────────────────────────────
 *  Ezek a függvények nem a bevallás adatairól szólnak, hanem arról, MIVEL és HOGYAN töltik ki.
 *  A sablonok ezekre hivatkoznak, amikor a kitöltés módjától függ valami – például a 2601 és a
 *  2608 `stat_bekuldes_modja` mezője, amely a NAV-nak jelzi, milyen eszközzel készült a bevallás.
 *  Eddig egyik sem volt megírva, ezért minden ilyen képlet kiértékelhetetlen maradt.
 */

/*  üzemmód(x) – „A" a szokásos kitöltés, „J" a NAV által visszaküldött bevallás javítása
 *  (a sablonokban csak ehhez a két értékhez hasonlítják). Nálunk mindig az előbbi. */
FUGGVENYEK['üzemmód'] = () => () => 'A';

/*  anyk_param(paraméter) – az ÁNYK környezeti paraméterei. A mi kitöltőnk WEBES, a bevallás
 *  szerkeszthető, és mindig ismerjük az adózót – ezek az igaz válaszok. Amit nem ismerünk, arra
 *  nil (nincs beállítva), nem üres szöveg: a sablonok a kettőt megkülönböztetik. */
const ANYK_PARAMOK: Record<string, string> = {
  'használati_mód': 'webes_kitöltés',
  'szerkesztés': 'I',
  'azonosítás_nélküli_üzemmód': '',
};
FUGGVENYEK['anyk_param'] = moho((v) => {
  const p = szoveg(v[0]);
  return Object.prototype.hasOwnProperty.call(ANYK_PARAMOK, p) ? ANYK_PARAMOK[p] : null;
});

/*  eredeti(x) – a mező betöltéskori, EREDETI értéke. A sablonokban mindig egy elágazás utolsó,
 *  „maradjon, ami volt" ága (`ha_n(…, …, eredeti("S"))`), és soha nem hasonlítják semmihez.
 *  Betöltéskori pillanatképet nem tartunk nyilván, ezért NINCS: a mezőhöz nem nyúlunk – ami
 *  pontosan az „eredeti érték marad" viselkedés. */
FUGGVENYEK['eredeti'] = () => () => NINCS;

/*  mainap(eltolás) – a mai dátum ÉÉÉÉHHNN számként, a megadott NAPPAL eltolva (a sablonok 324
 *  helyen 0-val vagy üresen hívják: „ma"). Dátumot adó hívásra nincs bizonyítékunk a
 *  jelentésre, és egy dátumot eltolásként értelmezni értelmetlen eredményt adna – ott inkább
 *  nem mondunk semmit. */
FUGGVENYEK['mainap'] = moho((v) => {
  const a = v[0];
  if (a !== null && a !== '' && typeof a !== 'number') return NINCS;
  const d = new Date();
  const ma = d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();
  const n = typeof a === 'number' ? Math.trunc(a) : 0;
  if (!n) return ma;
  if (Math.abs(n) > 3660) return NINCS;                 // ez nem eltolás, hanem dátum – ismeretlen jelentés
  return Number(napDatum(datumNapok(String(ma)) + n));
});

/*  dátumnév(dátum) – a dátum NEGYEDÉVE (1–4). A név megtévesztő, a jelentést a használat adja:
 *  a 2651-ben a `dátumnév(időszak vége) = 1` szabály üzenete szerint ez „a 2026-os év 01., 02.
 *  és 03. hónapjára benyújtott bevallás", a 2601-ben pedig a `dátumnév(tól) = dátumnév(ig)`
 *  azt vizsgálja, hogy a bevallási időszak egyetlen negyedéven belül van-e. */
FUGGVENYEK['dátumnév'] = moho((v) => {
  const sz = szoveg(v[0]).replace(/\D/g, '');
  if (sz.length < 6) return null;
  const ho = Number(sz.slice(4, 6));
  if (!(ho >= 1 && ho <= 12)) return null;
  return Math.floor((ho - 1) / 3) + 1;
});

/*  főadat(formId, fid) – egy mező értéke a FŐLAP (másik részbizonylat) első példányáról. A
 *  26HIPAK (helyi iparűzési adó) melléklapjai ezzel nézik meg a főlap beállításait. A
 *  form-közi olvasás mintája a `globsum`-é. */
FUGGVENYEK['főadat'] = (args, fk) => {
  const formNev = konstSzoveg(args[0]);
  const fid = konstSzoveg(args[1]);
  if (!formNev || !fid) return () => null;
  const e = fk.fordFormban(formNev, ['field', { s: fid }]);
  return (c) => {
    const f = c.m._form(formNev);
    if (!f) return null;
    const p = c.m._peldanyok(c.adat, f)[0];
    return e(c.m._ktx(c, f, 0, p && typeof p === 'object' ? p : {}));
  };
};


/* ── további ÁNYK-függvények (ellenőrző szabályokból) ──────────────────────────────────────
 *  Ezeket a NAV sablonjai a MEZŐELLENŐRZÉSEKBEN használják. A bevallás hibalistája nálunk a
 *  hatályos XSD-n alapul (a beküldött XML is abból készül), ezért ezek ma nem futnak – de ha
 *  egy szabály vegyesen számol és ellenőriz, a hiányuk az egész szabályt kiértékelhetetlenné
 *  tette. Csak olyat írunk meg, aminek a jelentése a sablonok használatából egyértelmű.
 */

/*  ^(a, b) – KIZÁRÓ VAGY. A sablonokban mindig logikai értékekre hívják, tehát nem hatványozás. */
FUGGVENYEK['^'] = moho((v) => {
  if (typeof v[0] !== 'boolean' || typeof v[1] !== 'boolean') return null;
  return v[0] !== v[1];
});

/** rightstr(szöveg, n) – az utolsó n karakter. */
FUGGVENYEK['rightstr'] = moho((v) => {
  const sz = szoveg(v[0]); const n = Math.trunc(Number(v[1]));
  if (!isFinite(n) || n < 0) return null;
  return n === 0 ? '' : sz.slice(Math.max(0, sz.length - n));
});

/** csakszámjegy(szöveg) – csupa számjegy-e (üres: igaz, mint a többi ÁNYK-ellenőrzésnél). */
FUGGVENYEK['csakszámjegy'] = moho((v) => {
  if (v[0] === null) return null;
  const sz = szoveg(v[0]).trim();
  return sz === '' ? true : /^\d+$/.test(sz);
});

/** jóemail(szöveg) – formailag helyes e-mail cím. */
FUGGVENYEK['jóemail'] = moho((v) => {
  if (v[0] === null) return null;
  const sz = szoveg(v[0]).trim();
  return sz === '' ? true : /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/.test(sz);
});

/*  negyedévutolsónapja(év, negyedév) – a negyedév utolsó napja ÉÉÉÉHHNN számként. A meglévő
 *  `hónaputolsónapja` párja; a sablonok a `dátumnév`-vel (= negyedév) együtt hívják. */
FUGGVENYEK['negyedévutolsónapja'] = moho((v) => {
  const ev = Math.trunc(Number(v[0])); const n = Math.trunc(Number(v[1]));
  if (!isFinite(ev) || !(n >= 1 && n <= 4)) return null;
  const ho = n * 3;
  for (let nap = 31; nap > 27; nap--) { const sz = String(ev * 10000 + ho * 100 + nap); if (joDatum(sz)) return +sz; }
  return 0;
});

/*  csatolmánydb("") – a küldeményhez csatolt fájlok száma. A kitöltőnk nem kezel csatolmányt,
 *  tehát pontosan nulla (nem „ismeretlen"). */
FUGGVENYEK['csatolmánydb'] = () => () => 0;

/*  tiltéstöröl(mező, "név") – ÁNYK-MŰVELET: levesz egy korábbi tiltást a mezőről. Nem állítás,
 *  hanem beavatkozás, és mindig sikerül – az ellenőrzés szempontjából igaz. */
FUGGVENYEK['tiltéstöröl'] = () => () => true;

/*  jószámlaszám / jóalszámlaszám – belföldi pénzforgalmi jelzőszám ellenőrzése. A 8 jegyű
 *  blokkok 9-7-3-1 súlyozású ellenőrző összege osztható 10-zel (MNB-szabvány): 2×8 vagy 3×8
 *  jegy. Az „alszámlaszám" a jelzőszám második fele, ugyanezzel a szabállyal. */
const szamlaCdv = (blokk: string) => {
  if (!/^\d{8}$/.test(blokk)) return false;
  const suly = [9, 7, 3, 1];
  let s = 0;
  for (let i = 0; i < 8; i++) s += Number(blokk[i]) * suly[i % 4];
  return s % 10 === 0;
};
const joSzamlaszam = (v: Ertek, reszek: number[]) => {
  if (v === null) return null;
  const sz = szoveg(v).replace(/[\s-]/g, '');
  if (!sz) return true;
  if (!reszek.includes(sz.length / 8) || !/^\d+$/.test(sz)) return false;
  for (let i = 0; i < sz.length; i += 8) if (!szamlaCdv(sz.slice(i, i + 8))) return false;
  return true;
};
FUGGVENYEK['jószámlaszám'] = moho((v) => joSzamlaszam(v[0], [2, 3]));
FUGGVENYEK['jóalszámlaszám'] = moho((v) => joSzamlaszam(v[0], [1, 2]));

/*  jó_iban(szöveg) – nemzetközi számlaszám az ISO 13616 szerint: az első négy karaktert a
 *  végére forgatva, a betűket 10..35-re cserélve a maradék 97-tel osztva 1. */
FUGGVENYEK['jó_iban'] = moho((v) => {
  if (v[0] === null) return null;
  const sz = szoveg(v[0]).replace(/\s/g, '').toUpperCase();
  if (!sz) return true;
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{10,30}$/.test(sz)) return false;
  const atrendez = sz.slice(4) + sz.slice(0, 4);
  let maradek = 0;
  for (const k of atrendez) {
    const jegyek = /\d/.test(k) ? k : String(k.charCodeAt(0) - 55);
    for (const j of jegyek) maradek = (maradek * 10 + Number(j)) % 97;
  }
  return maradek === 1;
});

/*  számjegy(érték, kezdet, hossz) – az érték SZÁMJEGYEIBŐL a `kezdet`-edik helytől `hossz` darab
 *  (1-alapú). Dátumból így emelik ki az évet (1,4) vagy a hónapot (5,2). Ha nincs annyi
 *  számjegy, nincs eredmény – a mezőt ilyenkor nem szabad kitölteni. */
FUGGVENYEK['számjegy'] = moho((v) => {
  const sz = szoveg(v[0]).replace(/\D/g, '');
  const kezd = Math.max(1, Math.trunc(Number(v[1])) || 1);
  const hossz = Math.max(0, Math.trunc(Number(v[2])) || 0);
  if (!sz || !hossz) return null;
  const r = sz.slice(kezd - 1, kezd - 1 + hossz);
  return r.length === hossz ? r : null;
});

/*  munkanap(dátum) – munkanap-e a megadott nap. A 26RENDNY így keresi meg a befizetési
 *  határidőt: `ha_n(munkanap(d-1), d-1, munkanap(d), d, …)` – az első olyan jelölt dátum kell,
 *  amelyik munkanap. Hétvége és munkaszüneti nap nem az; az áthelyezéseket a betöltött naptár
 *  adja (lásd `munkanaptarBeallit`). */
FUGGVENYEK['munkanap'] = moho((v, c) => {
  const sz = szoveg(v[0]).replace(/\D/g, '');
  if (sz.length !== 8) return null;
  return c.m._munkanapE(sz);
});

FUGGVENYEK['dpagecount'] = (args, fk) => {
  const a = fk.ford(args[0]);
  return (c) => {
    const nev = szoveg(a(c)).split('@')[0].trim().toLowerCase();
    if (!nev) return null;
    const lap = c.form.lapok.find((l) => szoveg(l.nev).toLowerCase() === nev);
    if (!lap) return null;
    return c.m.lapDbKtx(c, lap);
  };
};

/** keresés_mátrixban2 tényleges keresése (a mátrix a motorban él, ezért a moho-impl után itt pótoljuk). */
FUGGVENYEK['keresés_mátrixban2'] = (args, fk) => {
  const a = args.map((x) => fk.ford(x));
  return (c) => {
    const v = a.map((f) => f(c));
    if (v.length < 7 || typeof v[0] !== 'string' || typeof v[2] !== 'number' || typeof v[4] !== 'string') return null;
    const szurok: { oszlop: number; rel: string; ertek: string }[] = [];
    for (let i = 5; i + 1 < v.length; i += 2) {
      if (v[i] === null || v[i + 1] === null) break;
      szurok.push({ oszlop: Number(v[i]) - 1, rel: '=', ertek: szoveg(v[i + 1]) });
    }
    if (!szurok.length) return v[3];
    const sorok = matrixKeres(c.m, v[0], v[4], szurok, true);
    if (sorok === null) throw new AnykFutasiHiba('ismeretlen mátrix: ' + v[0]);
    if (!sorok.length) return v[3];
    return sorok[0][v[2] - 1] ?? '';
  };
};

function atfedes(args: any[], fk: Ford, kulcsos: boolean): Fv {
  const kezd = konstSzoveg(args[0]), veg = konstSzoveg(args[1]);
  const lapon = fk.ford(args[2]);
  const kulcsSpec = kulcsos ? konstSzoveg(args[3]) : null;
  const kf = kezd ? csoportFidek(fk.f, kezd) : [];
  const vf = veg ? csoportFidek(fk.f, veg) : [];
  const kl = kulcsSpec ? csoportFidek(fk.f, kulcsSpec) : [];
  if (!kf.length || kf.length !== vf.length) { fk.m._ismeretlenMezo(String(kezd)); return () => null; }
  return (c) => {
    const laponkent = lapon(c) === true;
    const i0 = c.form.mezok.get(kf[0])!;
    const db = c.m.lapDbKtx(c, i0.lap);
    const ertek = (fid: string, k: number): number | null => {
      const i = c.form.mezok.get(fid);
      if (!i) return null;
      const v = konstans(c.m.nyersKtx(c, i, k), i.tipus);
      return typeof v === 'number' ? v : (typeof v === 'string' && SZAM_RE.test(v) ? Number(v) : null);
    };
    const kulcs = (r: number, k: number): string => {
      if (!kl.length) return '';
      const i = c.form.mezok.get(kl[Math.min(r, kl.length - 1)]);
      return i ? (c.m.nyersKtx(c, i, k) ?? '') : '';
    };
    let csop = new Map<string, [number, number][]>();
    const vizsgal = (): boolean => {
      for (const l of csop.values()) for (let a = 0; a < l.length; a++) for (let b = a + 1; b < l.length; b++) {
        if (l[a][0] <= l[b][1] && l[b][0] <= l[a][1]) return false;
      }
      return true;
    };
    let ok = true;
    for (let k = 0; k < db; k++) {
      for (let r = 0; r < kf.length; r++) {
        let s = ertek(kf[r], k), e = ertek(vf[r], k);
        if (s === null && e === null) continue;
        if (s === null) s = e; if (e === null) e = s;
        const g = kulcs(r, k);
        if (!csop.has(g)) csop.set(g, []);
        csop.get(g)!.push([s as number, e as number]);
      }
      if (laponkent) { if (!vizsgal()) ok = false; csop = new Map(); }
    }
    if (!laponkent && !vizsgal()) ok = false;
    return ok;
  };
}
